import React, { useState, useEffect, useCallback, useRef } from "react";
import { Badge, Spin, Empty, Tooltip, message, Typography, Tag, Image, Modal, Select, DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  PhoneOutlined,
  CloseOutlined,
  AudioOutlined,
  AudioMutedOutlined,
  UserOutlined,
  DeleteOutlined,
  ContactsOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  ExpandOutlined,
  CompressOutlined,
  MinusOutlined,
  PhoneFilled,
  MessageOutlined,
  ArrowLeftOutlined,
  SendOutlined,
  PaperClipOutlined,
  LockOutlined,
  GlobalOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  SaveOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { request, useModel, useAccess } from "@umijs/max";
import { useTwilioDevice } from "./TwilioDeviceContext";
import { Call } from "@twilio/voice-sdk";
import { searchCustomerByPhone, getTenantContactsPaginated, getTenantConversation, sendTenantMessage, getCustomerRecentJobs, onSmsThreadUpdated } from "@/services/app/tenant/call-board";
import { getMyTwilioNo, getMyRecentCalls, saveCallLogNotes, checkCallNotesEligibility, recordCallActivity } from "@/services/app/twilio";
import { emitCallbackQueueUpdated, getCallQueueAssignments } from "@/services/app/tenant/call-queue";
import { getAllSmsTemplates } from "@/services/app/tenant/settings/smsTemplates";
import {
  getCallWrapupConfig,
  getPendingCallWrapups,
  openCallWrapup,
  saveCallWrapup,
  type Wrapup,
  type WrapupConfig,
  type WrapupOutcome,
} from "@/services/app/tenant/call-wrapup";
import { history } from "@umijs/max";
import { dtFormat, fmtTime } from "@/util";
import { fetchCallRecording } from "@/services/app/tenant/admin/live-calls";
import { SmsDeliveryStatusIndicator } from "@/components/SmsDeliveryStatus";
import TagDisplay from "@/components/Taxonomy/TagDisplay";
import { getStatusDescription, statusDescriptions } from "@/pages/tenant/dispatch/DispatchDashboard/utils/status";
import { DispatchColors } from "@/constants";
import { useLauncherDock, dockSlotInset } from "@/hooks/useLauncherDock";
import "./CallWidget.less";


interface CustomerInfo {
  id?: number;
  name?: string;
  phone?: string;
}

interface RecentCall {
  id: number;
  contact_phone: string;
  from_number: string;
  to_number: string;
  direction: string;
  call_status: string;
  duration: number | null;
  call_duration: number | null;
  notes?: string | null;
  created_at: string;
}

interface Contact {
  uid: string;
  id: number;
  name?: string;
  phone: string;
  phone_number: string;
  unread_count?: number;
}

interface QueueItem {
  id: number;
  customer_phone: string;
  reason: string;
  status: string;
  created_at: string;
}

const DIALPAD_KEYS = [
  { digit: "1", letters: "" },
  { digit: "2", letters: "ABC" },
  { digit: "3", letters: "DEF" },
  { digit: "4", letters: "GHI" },
  { digit: "5", letters: "JKL" },
  { digit: "6", letters: "MNO" },
  { digit: "7", letters: "PQRS" },
  { digit: "8", letters: "TUV" },
  { digit: "9", letters: "WXYZ" },
  { digit: "*", letters: "" },
  { digit: "0", letters: "+" },
  { digit: "#", letters: "" },
];

type TabKey = "dialpad" | "recents" | "contacts" | "queue";

// Max consecutive auto-retries after a failed call-notes save.
const NOTES_MAX_RETRIES = 3;

// Recording of the call being wrapped up — lets a CSR replay a call before
// recording the outcome late (overdue rows from the strip, or any wrap-up
// returned to after a few minutes). Twilio media needs account credentials,
// so the audio comes through our proxy as a blob; fetched once per log id and
// released on unmount. Only rendered when the server says a recording exists.
const WrapupRecordingPlayer: React.FC<{ logId: number }> = ({ logId }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setState("loading");
    setSrc(null);
    fetchCallRecording(logId)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
        setState("ready");
      })
      .catch(() => { if (!cancelled) setState("error"); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [logId]);
  return (
    <div className="cw-notes-popup__wrapup-row cw-notes-popup__recording">
      <span className="cw-notes-popup__wrapup-label">Recording</span>
      {state === "ready" && src ? (
        <audio className="cw-notes-popup__recording-audio" controls preload="metadata" src={src} />
      ) : (
        <span className="cw-notes-popup__recording-state">
          {state === "loading" ? "Loading recording…" : "Recording unavailable"}
        </span>
      )}
    </div>
  );
};

const CallWidget: React.FC = () => {
  const {
    twilioReady,
    twilioConnecting,
    myPhoneNumber,
    incomingCalls,
    activeCall,
    heldCalls,
    callDirection,
    makeCall: twilioMakeCall,
    acceptIncomingCall,
    rejectIncomingCall,
    hangupCall,
    clearIncomingCall,
    clearActiveCall,
    moveToHeld,
    reconnect,
  } = useTwilioDevice();

  const { initialState } = useModel("@@initialState");
  const csrId = initialState?.currentUser?.id;
  const access = useAccess();
  const isCallAvailable = (initialState?.currentUser?.is_call_availability ?? 1) !== 0;

  const [expanded, setExpanded] = useState(false);
  const [fullSize, setFullSize] = useState(false);
  const [panelPos, setPanelPos] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Outermost slot of the shared Call/Chat launcher dock (ChatWidget is slot 1).
  const dock = useLauncherDock(0);
  const dockBesideStyle = dock.besideStyle;
  const [activeTab, setActiveTab] = useState<TabKey>("dialpad");
  const [muted, setMuted] = useState(false);
  const [callTimer, setCallTimer] = useState(0);
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);
  const [callStatus, setCallStatus] = useState<"ringing" | "in-progress" | "ended" | null>(null);

  // Refs that mirror callStatus/callStartTime so the call lifecycle effect can
  // read the *current* value without being listed as a reactive dependency.
  // This prevents the effect from re-running (and incorrectly overriding state)
  // every time callStatus or callStartTime changes. 
  const callStatusRef = useRef<"ringing" | "in-progress" | "ended" | null>(null);
  callStatusRef.current = callStatus;
  const callStartTimeRef = useRef<Date | null>(null);
  callStartTimeRef.current = callStartTime;
  const [callEndReason, setCallEndReason] = useState<string | null>(null);
  const [holdLoading, setHoldLoading] = useState<"public" | "private" | null>(null);

  // Call notes (incoming & outgoing) — autosaved (debounced) while the call is
  // in progress and flushed when the call ends so nothing is lost on early
  // termination.
  const [callNotes, setCallNotes] = useState("");
  // "error" = save failed, auto-retry pending; "failed" = retries exhausted,
  // autosaving is stopped until the user edits or clicks Save again.
  const [notesSaveState, setNotesSaveState] = useState<"idle" | "saving" | "saved" | "error" | "failed">("idle");
  const notesCallSidRef = useRef<string | null>(null);
  const notesDraftRef = useRef("");
  const lastSavedNotesRef = useRef("");
  const notesSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Consecutive failed save attempts — auto-retry stops after NOTES_MAX_RETRIES.
  const notesRetryCountRef = useRef(0);
  // The notes popup opens when a call (either direction) is answered and stays
  // open — even after the call ends — until the user closes it, so notes can
  // be finished (and keep autosaving) after hangup.
  const [notesPopupOpen, setNotesPopupOpen] = useState(false);
  // Snapshot of the matched customer/job shown inside the notes popup — kept
  // separate from the live customerInfo/callerJobInfo so the job details stay
  // visible while finishing a note after the call ends (the live values are
  // cleared when the call state resets).
  const [notesJobInfo, setNotesJobInfo] = useState<{ customer: CustomerInfo; job: API.Dispatch | null } | null>(null);
  // Notes popup drag state — position persists across calls within the session.
  const [notesPopupPos, setNotesPopupPos] = useState<{ x: number; y: number } | null>(null);
  const [notesDragging, setNotesDragging] = useState(false);
  // Minimized = only the header bar (title, wrap-up timer, status, buttons)
  // stays on screen. Requested by the office once the wrap-up form made the
  // popup taller; the wrap-up itself stays open and keeps its timer.
  const [notesMinimized, setNotesMinimized] = useState(false);
  const notesPopupRef = useRef<HTMLDivElement>(null);
  const notesDragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);

  // ---- Call wrap-up (after-call work) ----
  // When an INBOUND agent-routed call ends, the CSR must close it with an
  // outcome + notes. The existing notes popup is reused as the wrap-up screen:
  // "wrap-up mode" is simply `wrapup !== null`.
  //
  // State machine (feature enabled only):
  //   idle ──(incoming call ends, open() says applicable)──▶ wrap-up pending
  //   idle ──(mount: pending() has an open row, e.g. page refresh)──▶ wrap-up pending
  //   pending ──(Save wrap-up 2xx)──▶ "Saved · label" flash ──▶ idle (+ next pending, if any)
  //   pending ──(X → confirm close)──▶ idle (row stays open server-side → admin exception)
  //   pending ──(new call answered)──▶ parked in the collapsed strip (draft kept) while the
  //             live call gets its own notes editor; re-expanded from the strip or by chaining
  //   pending ──(another call ends)──▶ its wrap-up joins the strip; chained after this one
  //
  // One wrap-up is expanded at a time; every other wrap-up the agent owes sits
  // in a collapsed strip inside the same popup (phone, call end time, Overdue
  // marker) and can be expanded with a click. Nothing is ever dropped from the
  // strip until it is saved or explicitly closed.
  const [wrapupConfig, setWrapupConfig] = useState<WrapupConfig | null>(null);
  const [wrapup, setWrapup] = useState<Wrapup | null>(null);
  // Mirrors so lifecycle effects can read the current wrap-up without
  // subscribing to it (same pattern as callStatusRef).
  const wrapupRef = useRef<Wrapup | null>(null);
  wrapupRef.current = wrapup;
  const wrapupEnabledRef = useRef(false);
  wrapupEnabledRef.current = !!wrapupConfig?.settings?.enabled;
  // SID the wrap-up save is keyed by — deliberately separate from
  // notesCallSidRef so a new call taking over the notes SID can never make
  // the wrap-up save target the wrong call.
  const wrapupCallSidRef = useRef<string | null>(null);
  // SIDs we already asked open() about — the "ended" effect can fire more
  // than once for the same call (status + direction + activeCall changes).
  const wrapupOpenAttemptedSidRef = useRef<Set<string>>(new Set());
  // Wrap-ups closed without an outcome this session — excluded when we look
  // for the next pending one, otherwise the same row would pop right back.
  const wrapupDismissedIdsRef = useRef<Set<number>>(new Set());
  const [wrapupOutcomeCode, setWrapupOutcomeCode] = useState<string | undefined>(undefined);
  const [wrapupCallbackDue, setWrapupCallbackDue] = useState<Dayjs | null>(null);
  const [wrapupError, setWrapupError] = useState<string | null>(null);
  const [wrapupSaving, setWrapupSaving] = useState(false);
  // Outcome label shown as "Saved · {label}" for ~1.5s before the popup closes.
  const [wrapupSavedLabel, setWrapupSavedLabel] = useState<string | null>(null);
  // Countdown: the deadline is computed from the server's own clock
  // (wrapup_ends_at - server_time) and then ticked locally, so client clock
  // skew does not matter.
  const wrapupDeadlineRef = useRef<number | null>(null);
  const [wrapupRemaining, setWrapupRemaining] = useState(0);
  // Wrap-ups this agent still owes besides the expanded one — the collapsed
  // strip. Rows are kept until saved/closed, including ones the server already
  // marked expired (a late save still works and clears the exception).
  const [wrapupQueue, setWrapupQueue] = useState<Wrapup[]>([]);
  // Drafts of parked editors, keyed "wrapup:<id>" (a collapsed wrap-up) or
  // "call:<sdk sid>" (the live call's notes while the CSR works on a queued
  // wrap-up mid-call). Restored when that editor is expanded again.
  type EditorDraft = {
    notes: string;
    outcomeCode?: string;
    callbackDue: Dayjs | null;
    jobInfo: { customer: CustomerInfo; job: API.Dispatch | null } | null;
  };
  const editorDraftsRef = useRef<Map<string, EditorDraft>>(new Map());
  // Mirrors of the expanded form so collapse can capture it from a callback.
  const wrapupFormRef = useRef<{ outcomeCode?: string; callbackDue: Dayjs | null; saved: boolean }>({ outcomeCode: undefined, callbackDue: null, saved: false });
  wrapupFormRef.current = { outcomeCode: wrapupOutcomeCode, callbackDue: wrapupCallbackDue, saved: !!wrapupSavedLabel };
  const notesJobInfoRef = useRef<typeof notesJobInfo>(null);
  notesJobInfoRef.current = notesJobInfo;
  const WRAPUP_QUEUE_MAX = 5;
  // const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>({ id: 10164, name: "Test Customer", phone: "+17406192979" }); // DEV TEST
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [callerJobInfo, setCallerJobInfo] = useState<API.Dispatch | null>(null);
  const [callerJobLoading, setCallerJobLoading] = useState(false);
  const [myPhoneNo, setMyPhoneNo] = useState<string | null>(null);

  // Dialpad
  const [dialNumber, setDialNumber] = useState("");
  const [dialLookup, setDialLookup] = useState<CustomerInfo | null>(null);
  const [dialCountryCode, setDialCountryCode] = useState<"+1" | "+91">("+1");
  const inputRef = useRef<HTMLInputElement>(null);

  // Recents
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [recentsLoading, setRecentsLoading] = useState(false);

  // Contacts
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState("");

  // Queue
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // SMS chat panel
  const [smsTarget, setSmsTarget] = useState<{ phone: string; name?: string } | null>(null);
  const [smsChatMessages, setSmsChatMessages] = useState<any[]>([]);
  const [smsChatLoading, setSmsChatLoading] = useState(false);
  const [smsChatText, setSmsChatText] = useState("");
  const [smsChatSending, setSmsChatSending] = useState(false);
  const [smsChatAttachments, setSmsChatAttachments] = useState<File[]>([]);
  const [smsChatPreviews, setSmsChatPreviews] = useState<string[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<API.SmsTemplate[]>([]);
  const [smsTemplateMediaUrls, setSmsTemplateMediaUrls] = useState<string[]>([]);
  const [smsSelectedTemplateId, setSmsSelectedTemplateId] = useState<number | undefined>(undefined);
  const smsTemplatesLoadedRef = useRef(false);
  const smsChatFileRef = useRef<HTMLInputElement>(null);
  const smsChatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const smsChatEndRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);

  const currentCall = activeCall || incomingCalls[0];
  const phoneNumber = currentCall?.customParameters?.get('customerPhone') || currentCall?.parameters?.From || currentCall?.parameters?.To || "";
  // Held calls are managed exclusively in the Hold Queue panel on the call board.
  // The widget returns to idle as soon as there is no active call or incoming call.
  const hasActiveInteraction = !!(incomingCalls.length || activeCall);

  // ---- Data fetching ----

  useEffect(() => {
    if (!phoneNumber || phoneNumber === "Unknown") {
      // Do NOT clear customerInfo here — the call lifecycle briefly produces an
      // empty phoneNumber during transitions (e.g. incoming→active, hold/resume).
      // The clear() function inside the callStatus==="ended" effect is the sole
      // place responsible for resetting customerInfo when the call truly ends.
      return;
    }
    let cancelled = false;
    searchCustomerByPhone(phoneNumber)
      .then((res) => { if (!cancelled) setCustomerInfo(res?.type === "customer" && res.data ? res.data : null); })
      .catch(() => { if (!cancelled) setCustomerInfo(null); });
    return () => { cancelled = true; };
  }, [phoneNumber]);

  useEffect(() => {
    if (!customerInfo?.id) {
      setCallerJobInfo(null);
      setCallerJobLoading(false);
      return;
    }
    let cancelled = false;
    setCallerJobLoading(true);
    getCustomerRecentJobs(customerInfo.id)
      .then((res) => {
        if (!cancelled) {
          const jobs: API.Dispatch[] = res?.data || [];
          setCallerJobInfo(jobs[0] || null);
        }
      })
      .catch(() => { if (!cancelled) setCallerJobInfo(null); })
      .finally(() => { if (!cancelled) setCallerJobLoading(false); });
    return () => { cancelled = true; };
  }, [customerInfo?.id]);

  useEffect(() => {
    if (dialNumber.length < 7) {
      setDialLookup(null);
      return;
    }
    const timer = setTimeout(() => {
      searchCustomerByPhone(dialNumber)
        .then((res) => setDialLookup(res?.type === "customer" && res.data ? res.data : null))
        .catch(() => setDialLookup(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [dialNumber]);

  // The CSR's own Twilio number is required by appointments.center's
  // assistant-action endpoint (`to`). It used to be fetched exactly once at
  // mount; a single failed request left `to` null for the life of the tab
  // (days), and every accept from that tab was rejected with 400 — the
  // customer was never bridged (Maddie, 2026-10-02). Now: retry at mount,
  // and ensureMyPhoneNo() fetches on demand right before a send.
  const myPhoneNoRef = useRef<string | null>(null);
  useEffect(() => { myPhoneNoRef.current = myPhoneNo; }, [myPhoneNo]);

  const ensureMyPhoneNo = useCallback(async (): Promise<string | null> => {
    if (myPhoneNoRef.current) return myPhoneNoRef.current;
    try {
      const res = await getMyTwilioNo();
      if (res?.phone_number) {
        myPhoneNoRef.current = res.phone_number;
        setMyPhoneNo(res.phone_number);
        return res.phone_number;
      }
    } catch (e) {
      console.warn("[CallWidget] own Twilio number lookup failed", e);
    }
    return null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    if (myPhoneNumber) {
      setMyPhoneNo(myPhoneNumber);
      return () => { cancelled = true; };
    }
    const attempt = async (n: number) => {
      const got = await ensureMyPhoneNo();
      if (cancelled || got || n >= 5) return;
      // 5 s, 10 s, 20 s, 40 s, 80 s — covers a slow login burst without hammering.
      timer = window.setTimeout(() => attempt(n + 1), 5000 * 2 ** n);
    };
    attempt(0);
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, [myPhoneNumber, ensureMyPhoneNo]);

  const loadRecents = useCallback(async () => {
    setRecentsLoading(true);
    try {
      const res = await getMyRecentCalls(30);
      setRecentCalls(res?.calls || []);
    } catch {
      setRecentCalls([]);
    } finally {
      setRecentsLoading(false);
    }
  }, []);

  const loadContacts = useCallback(async (search = "") => {
    setContactsLoading(true);
    try {
      const res = await getTenantContactsPaginated({ page: 1, limit: 30, search });
      setContacts(res?.contacts || []);
    } catch {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const res = await getCallQueueAssignments({ limit: 20, status: "pending" });
      setQueueItems(res?.assignments || []);
      setPendingCount(res?.pending_count || 0);
    } catch {
      setQueueItems([]);
    } finally {
      setQueueLoading(false);
    }
  }, []);

  // Load tab data when switching
  useEffect(() => {
    if (!expanded) return;
    if (activeTab === "recents") loadRecents();
    if (activeTab === "contacts") loadContacts(contactSearch);
    if (activeTab === "queue") loadQueue();
  }, [activeTab, contactSearch, expanded, loadContacts, loadQueue, loadRecents]);

  // Contact search debounce
  useEffect(() => {
    if (activeTab !== "contacts" || !expanded) return;
    const t = setTimeout(() => loadContacts(contactSearch), 300);
    return () => clearTimeout(t);
  }, [activeTab, contactSearch, expanded, loadContacts]);

  // ---- Outgoing-call notes autosave ----
  // Returns true when the draft is persisted (or there was nothing to save),
  // false when the save failed — callers like closeNotesPopup use this to
  // decide whether it is safe to discard the draft.
  const flushCallNotes = useCallback(async (): Promise<boolean> => {
    if (notesSaveTimerRef.current) {
      clearTimeout(notesSaveTimerRef.current);
      notesSaveTimerRef.current = null;
    }
    const callSid = notesCallSidRef.current;
    const notes = notesDraftRef.current;
    if (!callSid || notes === lastSavedNotesRef.current) return true;
    setNotesSaveState("saving");
    try {
      const res = await saveCallLogNotes({ call_sid: callSid, notes });
      // For incoming calls the SDK's CallSid is the child leg; the backend
      // resolves it to the parent-leg SID the log row is keyed by and returns
      // it — reuse it so subsequent saves skip the REST lookup. Guarded so a
      // new call started during the await isn't clobbered.
      const resolvedSid: string | undefined = res?.data?.call_sid;
      // Still the same editor? A new call or a parked/expanded wrap-up may
      // have taken the editor over during the await — then this result must
      // not stamp that editor's baseline or status.
      const sameEditor = notesCallSidRef.current === callSid || (!!resolvedSid && notesCallSidRef.current === resolvedSid);
      if (resolvedSid && notesCallSidRef.current === callSid) {
        notesCallSidRef.current = resolvedSid;
      }
      if (sameEditor) {
        lastSavedNotesRef.current = notes;
        notesRetryCountRef.current = 0;
        setNotesSaveState("saved");
      }
      return true;
    } catch {
      // Keep the draft dirty so the next flush sends it again, and auto-retry
      // up to NOTES_MAX_RETRIES times (covers the webhook race where the
      // call-log row does not exist yet during the first seconds of the call).
      // After that, stop — new edits or a manual Save restart the cycle.
      if (notesRetryCountRef.current < NOTES_MAX_RETRIES) {
        notesRetryCountRef.current += 1;
        setNotesSaveState("error");
        if (!notesSaveTimerRef.current) {
          notesSaveTimerRef.current = setTimeout(() => flushCallNotes(), 3000);
        }
      } else {
        setNotesSaveState("failed");
      }
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNotesChange = (value: string) => {
    setCallNotes(value);
    notesDraftRef.current = value;
    // New edits restart the retry budget so autosaving resumes after "failed".
    notesRetryCountRef.current = 0;
    if (notesSaveTimerRef.current) clearTimeout(notesSaveTimerRef.current);
    notesSaveTimerRef.current = setTimeout(() => flushCallNotes(), 1000);
  };

  const resetCallNotes = useCallback(() => {
    if (notesSaveTimerRef.current) {
      clearTimeout(notesSaveTimerRef.current);
      notesSaveTimerRef.current = null;
    }
    notesCallSidRef.current = null;
    notesDraftRef.current = "";
    lastSavedNotesRef.current = "";
    notesRetryCountRef.current = 0;
    setCallNotes("");
    setNotesSaveState("idle");
    setNotesJobInfo(null);
  }, []);

  // Capture the call SID once the call is answered — for outbound browser
  // calls the SDK only populates parameters.CallSid after "accept". The notes
  // popup opens for both incoming and outgoing answered calls. For incoming
  // calls it is gated on a backend eligibility check first: the receiving
  // side of an internal agent-to-agent call shares the caller's single
  // call-log row, so showing the editor there would overwrite the caller's
  // notes.
  const openNotesForCall = useCallback((sid: string, direction: string | null) => {
    notesCallSidRef.current = sid;
    // A new call's notes always open expanded.
    setNotesMinimized(false);
    if (direction !== "incoming") {
      setNotesPopupOpen(true);
      return;
    }
    checkCallNotesEligibility(sid)
      .then((res) => {
        // A new call may have taken over while the check was in flight.
        if (notesCallSidRef.current !== sid) return;
        if (res?.data?.allowed === false) {
          notesCallSidRef.current = null;
          return;
        }
        // Prime the parent-leg SID so the first autosave skips the REST walk.
        if (res?.data?.call_sid) {
          notesCallSidRef.current = res.data.call_sid;
        }
        setNotesPopupOpen(true);
      })
      .catch(() => {
        // Fail open — a transient check failure shouldn't block note-taking
        // on regular incoming calls; the save endpoint still owns the data.
        if (notesCallSidRef.current === sid) setNotesPopupOpen(true);
      });
  }, []);

  // The "open the notes editor once a call is answered" effect lives below
  // the wrap-up helpers (it parks a pending wrap-up via collapseCurrentWrapup).

  // Keep the notes popup's job snapshot in sync while the call is live. The
  // customerInfo guard means the snapshot survives the end-of-call clear, and
  // the in-progress guard stops a *ringing* call (whose customer lookup runs
  // before accept) from overwriting the job details of a still-open note
  // from the previous call. A pending wrap-up keeps its own snapshot: a newer
  // live call must not replace the job details of the call being wrapped up.
  useEffect(() => {
    if (notesPopupOpen && customerInfo && callStatus === "in-progress" && !wrapup) {
      setNotesJobInfo({ customer: customerInfo, job: callerJobInfo });
    }
  }, [notesPopupOpen, customerInfo, callerJobInfo, callStatus, wrapup]);

  const closeNotesPopup = async () => {
    // Persist any unsaved draft before closing; if the save fails, ask for
    // explicit confirmation before the draft is discarded so the note is
    // never silently lost.
    const saved = await flushCallNotes();
    if (!saved) {
      Modal.confirm({
        title: "Call notes could not be saved",
        content: "Close anyway? The unsaved note will be discarded.",
        okText: "Close & discard",
        okButtonProps: { danger: true },
        cancelText: "Keep editing",
        onOk: () => {
          resetCallNotes();
          setNotesPopupOpen(false);
        },
      });
      return;
    }
    resetCallNotes();
    setNotesPopupOpen(false);
  };

  // Flush any unsaved notes when the component unmounts mid-call.
  useEffect(() => () => { flushCallNotes(); }, [flushCallNotes]);

  // ---- Call wrap-up helpers ----
  // Mirrors used by finishWrapup, which can run from a timer well after the
  // closure that scheduled it was created.
  const activeCallRef = useRef<Call | null>(null);
  activeCallRef.current = activeCall;
  const callDirectionRef = useRef<typeof callDirection>(callDirection);
  callDirectionRef.current = callDirection;

  // Outcomes offered on the phone channel, in display order.
  const voiceOutcomes = React.useMemo<WrapupOutcome[]>(
    () =>
      (wrapupConfig?.outcomes || [])
        .filter((o) => o.is_active && (o.allowed_channels || []).includes("voice"))
        .sort((a, b) => a.sort_order - b.sort_order),
    [wrapupConfig],
  );
  const selectedWrapupOutcome = React.useMemo(
    () => voiceOutcomes.find((o) => o.code === wrapupOutcomeCode) ?? null,
    [voiceOutcomes, wrapupOutcomeCode],
  );
  const wrapupNotesMissing = !!selectedWrapupOutcome?.requires_notes && !callNotes.trim();
  const canSaveWrapup = !!selectedWrapupOutcome && !wrapupNotesMissing && !wrapupSaving && !wrapupSavedLabel;

  const clearWrapupState = useCallback(() => {
    wrapupRef.current = null;
    wrapupCallSidRef.current = null;
    wrapupDeadlineRef.current = null;
    setWrapup(null);
    setWrapupOutcomeCode(undefined);
    setWrapupCallbackDue(null);
    setWrapupError(null);
    setWrapupSaving(false);
    setWrapupSavedLabel(null);
    setWrapupRemaining(0);
  }, []);

  // Switch the notes popup into wrap-up mode for `w`. `prefillNotes` is used
  // for wrap-ups recovered from the server (page refresh / chained pending
  // rows) where the widget holds no draft; for a call that just ended live
  // the draft already in the editor is the wrap-up's notes.
  const enterWrapupMode = useCallback((w: Wrapup, opts: { prefillNotes: boolean }) => {
    wrapupRef.current = w;
    wrapupCallSidRef.current = w.call_sid;
    // Notes autosave now targets the wrap-up's (parent-leg) SID.
    notesCallSidRef.current = w.call_sid;
    // A draft parked when this wrap-up was collapsed wins over server notes.
    const draft = editorDraftsRef.current.get(`wrapup:${w.id}`);
    editorDraftsRef.current.delete(`wrapup:${w.id}`);
    if (opts.prefillNotes) {
      const notes = draft?.notes ?? w.notes ?? "";
      notesDraftRef.current = notes;
      // A parked draft may not have reached the server — leave it dirty so
      // the next flush sends it; server-sourced notes are already saved.
      lastSavedNotesRef.current = draft ? "" : notes;
      notesRetryCountRef.current = 0;
      setCallNotes(notes);
      setNotesSaveState("idle");
      // Job snapshot: the parked one, or none (no live call to read from).
      setNotesJobInfo(draft?.jobInfo ?? null);
    }
    setWrapupQueue((q) => q.filter((x) => x.id !== w.id));
    const remainingMs = dayjs(w.wrapup_ends_at).diff(dayjs(w.server_time));
    wrapupDeadlineRef.current = Date.now() + Math.max(0, remainingMs);
    setWrapupRemaining(Math.max(0, Math.ceil(remainingMs / 1000)));
    setWrapup(w);
    setWrapupOutcomeCode(draft?.outcomeCode ?? w.outcome_code ?? undefined);
    setWrapupCallbackDue(draft?.callbackDue ?? null);
    setWrapupError(null);
    setWrapupSaving(false);
    setWrapupSavedLabel(null);
    // Force the popup open (and expanded) even if the user closed or
    // minimized the plain notes popup earlier.
    setNotesMinimized(false);
    setNotesPopupOpen(true);
  }, []);

  // Everything this agent still owes, newest first, minus rows dismissed in
  // this session. The server includes never-saved rows that already expired,
  // so a wrap-up that waited behind another one is never dropped.
  const fetchPendingWrapups = useCallback(async (): Promise<Wrapup[]> => {
    const res = await getPendingCallWrapups();
    const rows = (res?.data?.wrapups || []).filter((w) => !wrapupDismissedIdsRef.current.has(w.id));
    rows.sort((a, b) => String(b.wrapup_started_at).localeCompare(String(a.wrapup_started_at)));
    return rows;
  }, []);

  // Refresh the collapsed strip without touching the expanded wrap-up.
  const refreshWrapupQueue = useCallback(async () => {
    if (!wrapupEnabledRef.current) return;
    try {
      const rows = await fetchPendingWrapups();
      setWrapupQueue(rows.filter((w) => w.id !== wrapupRef.current?.id));
    } catch (e) {
      console.warn("[CallWrapup] failed to refresh pending wrap-ups", e);
    }
  }, [fetchPendingWrapups]);

  // Expand the newest pending wrap-up, if any (page refresh recovery on
  // mount, and chaining after one wrap-up is resolved); the rest fill the
  // strip. Returns true when one was opened.
  const loadNextPendingWrapup = useCallback(async (): Promise<boolean> => {
    if (!wrapupEnabledRef.current || wrapupRef.current) return false;
    try {
      const rows = await fetchPendingWrapups();
      if (wrapupRef.current) return false;
      if (!rows.length) {
        setWrapupQueue([]);
        return false;
      }
      const [next, ...rest] = rows;
      setWrapupQueue(rest);
      enterWrapupMode(next, { prefillNotes: true });
      return true;
    } catch (e) {
      console.warn("[CallWrapup] failed to load pending wrap-ups", e);
      return false;
    }
  }, [enterWrapupMode, fetchPendingWrapups]);

  // Park the expanded wrap-up in the strip (draft, outcome, callback due and
  // job snapshot kept) and leave wrap-up mode. Used when a new call is
  // answered and when the CSR switches to another queued wrap-up.
  const collapseCurrentWrapup = useCallback(() => {
    const w = wrapupRef.current;
    if (!w) return;
    // Already saved (the "Saved · label" flash before finishWrapup runs):
    // nothing to park — just leave wrap-up mode.
    if (!wrapupFormRef.current.saved) {
      // Persist the draft under this wrap-up's SID before the editor is
      // reused (flush reads the refs synchronously).
      flushCallNotes();
      editorDraftsRef.current.set(`wrapup:${w.id}`, {
        notes: notesDraftRef.current,
        outcomeCode: wrapupFormRef.current.outcomeCode,
        callbackDue: wrapupFormRef.current.callbackDue,
        jobInfo: notesJobInfoRef.current,
      });
      setWrapupQueue((q) => (q.some((x) => x.id === w.id) ? q : [w, ...q]));
    }
    resetCallNotes();
    clearWrapupState();
  }, [flushCallNotes, resetCallNotes, clearWrapupState]);

  // Expand a queued wrap-up. Whatever the editor holds now is parked first:
  // another wrap-up (back to the strip) or the live call's notes (restored by
  // finishWrapup once this wrap-up is saved or closed).
  const switchToWrapup = useCallback((w: Wrapup) => {
    if (wrapupRef.current?.id === w.id) return;
    if (wrapupRef.current) {
      collapseCurrentWrapup();
    } else if (notesCallSidRef.current) {
      const liveSid = activeCallRef.current?.parameters?.CallSid || notesCallSidRef.current;
      flushCallNotes();
      editorDraftsRef.current.set(`call:${liveSid}`, {
        notes: notesDraftRef.current,
        outcomeCode: undefined,
        callbackDue: null,
        jobInfo: notesJobInfoRef.current,
      });
      resetCallNotes();
    }
    enterWrapupMode(w, { prefillNotes: true });
  }, [collapseCurrentWrapup, enterWrapupMode, flushCallNotes, resetCallNotes]);

  // Leave wrap-up mode (after save or confirmed close). With a call live
  // right now the editor goes back to that call (restoring the draft parked
  // when the CSR switched away mid-call) and queued wrap-ups stay in the
  // strip; otherwise the next pending wrap-up is chained.
  const finishWrapup = useCallback(async () => {
    resetCallNotes();
    setNotesPopupOpen(false);
    clearWrapupState();
    const live = activeCallRef.current;
    if (callStatusRef.current === "in-progress" && live?.parameters?.CallSid) {
      const sid = live.parameters.CallSid;
      openNotesForCall(sid, callDirectionRef.current);
      const draft = editorDraftsRef.current.get(`call:${sid}`);
      if (draft) {
        editorDraftsRef.current.delete(`call:${sid}`);
        notesDraftRef.current = draft.notes;
        lastSavedNotesRef.current = "";
        setCallNotes(draft.notes);
        setNotesJobInfo(draft.jobInfo);
      }
      void refreshWrapupQueue();
      return;
    }
    await loadNextPendingWrapup();
  }, [resetCallNotes, clearWrapupState, loadNextPendingWrapup, openNotesForCall, refreshWrapupQueue]);

  // Open the notes editor once a call is answered. A wrap-up still expanded
  // from a previous call is parked in the strip first (draft kept) so the
  // live call gets its own editor and nothing typed during this call lands
  // on the previous call's wrap-up.
  useEffect(() => {
    if (callStatus !== "in-progress" || !activeCall?.parameters?.CallSid) return;
    if (wrapupRef.current) collapseCurrentWrapup();
    openNotesForCall(activeCall.parameters.CallSid, callDirection);
  }, [callStatus, activeCall, callDirection, openNotesForCall, collapseCurrentWrapup]);

  const handleWrapupOutcomeChange = (code: string) => {
    setWrapupOutcomeCode(code);
    setWrapupError(null);
    const o = voiceOutcomes.find((x) => x.code === code);
    setWrapupCallbackDue(
      o?.next_action_type === "callback" ? dayjs().add(o.default_callback_due_minutes ?? 0, "minute") : null,
    );
  };

  const handleWrapupSave = async () => {
    const sid = wrapupCallSidRef.current;
    if (!wrapupRef.current || !sid || !selectedWrapupOutcome || !canSaveWrapup) return;
    setWrapupError(null);
    setWrapupSaving(true);
    // Persist the call notes first (same text is sent as the wrap-up notes);
    // a failed notes flush must not block closing the wrap-up.
    notesRetryCountRef.current = 0;
    await flushCallNotes();
    try {
      const res = await saveCallWrapup({
        call_sid: sid,
        outcome_code: selectedWrapupOutcome.code,
        notes: notesDraftRef.current,
        ...(selectedWrapupOutcome.next_action_type === "callback" && wrapupCallbackDue
          ? { callback_due_at: wrapupCallbackDue.toISOString() }
          : {}),
      });
      const label = res?.data?.wrapup?.outcome_label || selectedWrapupOutcome.label;
      setWrapupSaving(false);
      // A "callback" outcome lands on THIS agent's private callback list (not the
      // shared board, which has no WS push for private rows) — nudge the nav
      // badge to re-fetch so its "N mine" count updates right away.
      if (selectedWrapupOutcome.next_action_type === "callback") emitCallbackQueueUpdated();
      setWrapupSavedLabel(selectedWrapupOutcome.next_action_type === "callback" ? `${label} · added to My Callbacks` : label);
      setTimeout(() => { finishWrapup(); }, 1500);
    } catch (err: any) {
      // 422 validation (message + fields), 403 (another agent's wrap-up), 404
      // (nothing open) all carry a `message`; shown inline, popup stays open.
      setWrapupSaving(false);
      const data = err?.response?.data;
      setWrapupError(
        (typeof data?.message === "string" && data.message) || err?.message || "Failed to save wrap-up.",
      );
    }
  };

  // X in wrap-up mode: never silently discard — the row stays open on the
  // server and surfaces as an admin exception.
  const closeWrapupPopup = () => {
    Modal.confirm({
      title: "Close without saving an outcome?",
      content: "This call will be listed as a missing wrap-up for an admin to fix.",
      okText: "Close",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      onOk: async () => {
        const w = wrapupRef.current;
        if (w) wrapupDismissedIdsRef.current.add(w.id);
        // Keep the notes autosave behaviour: persist the draft before closing.
        await flushCallNotes();
        await finishWrapup();
      },
    });
  };

  // Load the wrap-up config once; when enabled, recover any wrap-up left open
  // (e.g. the agent refreshed the page mid wrap-up).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getCallWrapupConfig();
        if (cancelled) return;
        const cfg = res?.data;
        if (!cfg?.settings) return;
        setWrapupConfig(cfg);
        // Set synchronously so the pending lookup below doesn't wait a render.
        wrapupEnabledRef.current = !!cfg.settings.enabled;
        if (cfg.settings.enabled) await loadNextPendingWrapup();
      } catch (e) {
        console.warn("[CallWrapup] failed to load config", e);
      }
    })();
    return () => { cancelled = true; };
  }, [loadNextPendingWrapup]);

  // Countdown tick while a wrap-up is pending.
  const wrapupId = wrapup?.id ?? null;
  useEffect(() => {
    if (wrapupId === null) return;
    const tick = () => {
      const d = wrapupDeadlineRef.current;
      setWrapupRemaining(d ? Math.max(0, Math.ceil((d - Date.now()) / 1000)) : 0);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [wrapupId]);

  // ---- Assistant action notify ----
  // Tells appointments.center what the CSR did with a routed call. "accepted"
  // is the message that makes them bridge the customer to this CSR — if it is
  // rejected the CSR sits on a silent leg (30 days to 2026-10-02: 140 of
  // ~1,970 accepts failed; 125 "Call not found", 12 "from/to required").
  // Hardened accordingly:
  //   - names the call by the SIDs they know (parentCallSid = the assistant
  //     leg they dialed into Butler, customerCallSid = their inbound call),
  //     passed down as <Client> parameters by TwilioApi::handleTwilioCall,
  //     alongside the browser-leg SID they historically received;
  //   - never sends a null `to`: fetches the CSR's number on demand and, as
  //     a last resort, repeats `from` so their validation passes;
  //   - retries once after 1 s on network errors / 5xx (their lock timeouts),
  //     never on 4xx.
  const notifyAssistantAction = useCallback(
    async (action: "accepted" | "rejected" | "hangup", call?: Call) => {
      const targetCall = call || activeCall || incomingCalls[0];
      const callSid = targetCall?.parameters?.CallSid;
      if (!callSid) return;

      const ownNumber = myPhoneNoRef.current || (await ensureMyPhoneNo());
      const from = targetCall?.customParameters?.get("customerPhone") || targetCall?.parameters?.From || ownNumber || null;
      const to = ownNumber || from;
      const parentCallSid = targetCall?.customParameters?.get("parentCallSid") || null;
      const customerCallSid = targetCall?.customParameters?.get("customerCallSid") || null;
      const assistantCallSid = targetCall?.customParameters?.get("assistantCallSid") || null;

      const requestBody = {
        // Browser-leg SID — kept first for backwards compatibility with their
        // current lookup; the SIDs below are the ones they can match directly.
        callSid,
        clientCallSid: callSid,
        parentCallSid,
        customerCallSid,
        assistantCallSid,
        action,
        from,
        to,
        toMissing: !ownNumber, // true when `to` is the `from` fallback
        accountSid: targetCall?.parameters?.AccountSid,
        toAlias: targetCall?.parameters?.To,
        stirStatus: targetCall?.parameters?.StirStatus,
        clientTime: new Date().toISOString(),
      };
      const eventBase = {
        call_sid: callSid,
        customer_phone: targetCall?.customParameters?.get("customerPhone") || targetCall?.parameters?.From,
        call_source: targetCall?.customParameters?.get("callSource"),
      };

      const send = () =>
        fetch(`${APPT_CENTER_API_URL}/api/assistant-action`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Admin-Token": "admin-1234-OK",
          },
          body: JSON.stringify(requestBody),
        });

      let attempts = 0;
      let lastStatus: number | null = null;
      let lastBody = "";
      let lastError: unknown = null;
      while (attempts < 2) {
        attempts += 1;
        try {
          const res = await send();
          lastStatus = res.status;
          lastBody = await res.text().catch(() => "");
          lastError = null;
          if (res.ok) {
            // Delivery receipt: what we sent and what they answered. When their
            // 200 later contradicts the rotation result, this is the evidence
            // that proves ACTION_ENDPOINT_LOST against the Appointment Center.
            recordCallActivity({
              event_type: "action_forwarded",
              ...eventBase,
              details: { action, status: res.status, attempts, request: requestBody, response: lastBody.slice(0, 2000) },
            });
            return;
          }
          if (res.status < 500) break; // 4xx will not improve on retry
        } catch (error) {
          lastError = error;
          lastStatus = null;
        }
        if (attempts < 2) await new Promise((r) => setTimeout(r, 1000));
      }

      // Evidence for the attribution engine: the CSR acted, but the
      // Appointment Center was never told — their status for this call is
      // now stale through no fault of the CSR.
      console.error("[AssistantAction] failed", lastStatus, lastBody || lastError);
      recordCallActivity({
        event_type: "notify_failed",
        ...eventBase,
        details: {
          stage: "assistant_action",
          action,
          attempts,
          ...(lastStatus !== null ? { status: lastStatus, response: lastBody.slice(0, 2000) } : {}),
          ...(lastError ? { error: String((lastError as any)?.message || lastError) } : {}),
          request: requestBody,
        },
      });
    },
    [activeCall, incomingCalls, ensureMyPhoneNo],
  );

  // ---- Call lifecycle ----
  useEffect(() => {
    if (!activeCall) return;

    // Snapshot the SDK status synchronously.
    const s = activeCall.status();
    if (s === "closed") {
      // Do NOT override an explicitly-confirmed "in-progress" resume.
      // For private-hold resumes the Twilio SDK call object can briefly (or
      // permanently) report "closed" because the server tears down the CSR's
      // conference leg during the hold and re-bridges on resume.  If resumeCall
      // already set "in-progress" we trust the API response over the SDK snapshot;
      // the real end-of-call will arrive via the onDisconnect event below.
      if (callStatusRef.current !== "in-progress") {
        setCallStatus("ended");
        setCallEndReason("Call ended");
      }
    } else if (s === "open") {
      setCallStatus("in-progress");
      // Use the ref so we don't need callStartTime in the dep array (which would
      // cause a spurious re-run — and a second status() check — whenever
      // callStartTime is set by resumeCall or onAccept).
      if (!callStartTimeRef.current) setCallStartTime(new Date());
    } else {
      // The call's SDK status is transitional (e.g. "pending" / "connecting").
      // Do NOT override an already-confirmed "in-progress" status — this is the
      // exact race that fires when resumeFromHeld restores an on-hold call whose
      // WebRTC leg is momentarily in a connecting state.  Only set "ringing" when
      // we know the call has not yet been answered.
      if (callStatusRef.current !== "in-progress") {
        setCallStatus("ringing");
      }
    }

    const onAccept = () => {
      setCallStatus("in-progress");
      setCallStartTime(new Date());
      setCallEndReason(null);
    };
    const onDisconnect = () => {
      setCallStatus("ended");
      setCallEndReason("Call ended");
    };
    const onCancel = () => {
      setCallStatus("ended");
      setCallEndReason("Call cancelled");
    };
    const onReject = () => {
      setCallStatus("ended");
      setCallEndReason("Call rejected");
    };
    const onError = (e: any) => {
      setCallStatus("ended");
      setCallEndReason(`Error: ${e?.message || "Unknown"}`);
    };

    activeCall.on("accept", onAccept);
    activeCall.on("disconnect", onDisconnect);
    activeCall.on("cancel", onCancel);
    activeCall.on("reject", onReject);
    activeCall.on("error", onError);
    return () => {
      activeCall.off("accept", onAccept);
      activeCall.off("disconnect", onDisconnect);
      activeCall.off("cancel", onCancel);
      activeCall.off("reject", onReject);
      activeCall.off("error", onError);
    };
    // callStartTime intentionally omitted — accessed via callStartTimeRef so that
    // a callStartTime change (e.g. from resumeCall) does not re-trigger this effect
    // and cause a second, potentially incorrect, status() snapshot.
    // callStatus intentionally omitted — accessed via callStatusRef for the same reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall]);

  // When the last waiting call disappears with no active call in progress,
  // show "Caller hung up" (the self-cleaning listener in the context handles
  // the actual array removal; we just watch the length transition here).
  const prevIncomingLenRef = useRef(0);
  useEffect(() => {
    const prev = prevIncomingLenRef.current;
    prevIncomingLenRef.current = incomingCalls.length;
    if (prev > 0 && incomingCalls.length === 0 && !activeCall) {
      setCallStatus("ended");
      setCallEndReason("Caller hung up");
    }
  }, [incomingCalls.length, activeCall]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (callStartTime && callStatus === "in-progress") {
      interval = setInterval(() => setCallTimer(Math.floor((Date.now() - callStartTime.getTime()) / 1000)), 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callStartTime, callStatus]);

  useEffect(() => {
    if (incomingCalls.length > 0) {
      setExpanded(true);
      // Only reset active-call state when there is no call already in progress.
      // If the CSR is on a call, preserve callStatus/callTimer so the active-call
      // screen stays intact — the call-waiting list will appear alongside it.
      if (!activeCall) {
        setCallStatus("ringing");
        setCallEndReason(null);
        setCallStartTime(null);
        setCallTimer(0);
        setMuted(false);
      }
    }
  }, [incomingCalls.length, activeCall]);

  useEffect(() => {
    if (!activeCall) return;
    if (callDirection === "outgoing") {
      setExpanded(true);
      setCallEndReason(null);
      setCallStartTime(null);
      setCallTimer(0);
      setMuted(false);
    }
    // A new call (either direction) starts a fresh note — save any unsent
    // draft from a still-open previous note first (flush reads the refs
    // synchronously).
    flushCallNotes();
    // Exception: an expanded wrap-up still owns the notes editor at this point
    // (activeCall is set before the call is answered). It is parked — draft
    // kept — by the in-progress effect below the wrap-up helpers, so it must
    // not be wiped here.
    if (!wrapupRef.current) resetCallNotes();
  }, [activeCall, callDirection, flushCallNotes, resetCallNotes]);

  useEffect(() => {
    if (callStatus === "ended") {
      // Persist whatever was typed the moment the call ends (normal hangup,
      // remote disconnect, cancel, error). The notes popup itself stays open
      // and keeps autosaving until the user closes it.
      flushCallNotes();
      const clear = () => {
        setCallStatus(null);
        setCallEndReason(null);
        setCallStartTime(null);
        setCallTimer(0);
        setMuted(false);
        setCustomerInfo(null);
        setCallerJobInfo(null);
        clearActiveCall();
      };
      // If there are already incoming calls waiting, clear the ended state
      // immediately so the full Accept/Decline UI can surface without delay.
      // This covers the multi-call public-hold resume path: resumeFromHeld
      // restores activeCall to the old held leg; the server then tears down
      // that leg (firing "ended") while the new incoming resume callback is
      // already in incomingCalls — clearing activeCall at once lets the
      // !activeCall guard show Accept/Decline instead of the ended screen.
      if (incomingCalls.length > 0) {
        clear();
        return;
      }
      const timer = setTimeout(clear, 3000);
      return () => clearTimeout(timer);
    }
  }, [callStatus, clearActiveCall, incomingCalls.length, flushCallNotes]);

  // Resolve which SID identifies the ended call for the wrap-up open request.
  // notesCallSidRef is primed with the parent-leg SID by the eligibility
  // check; while a wrap-up is pending it still points at THAT call, so the
  // ended call's own SID must come from the SDK instead (the backend
  // resolves child-leg SIDs).
  const wrapupSidForCall = useCallback((call: Call | null): string | null => {
    const sdkSid = call?.parameters?.CallSid || null;
    return wrapupRef.current ? sdkSid : notesCallSidRef.current || sdkSid;
  }, []);

  // Ask the backend whether the call that just ended needs a wrap-up
  // (agent-routed inbound calls only — the backend decides) and, if so, enter
  // wrap-up mode. Idempotent per SID: the remote-hangup effect and the local
  // hangup button can both call this for the same call.
  const requestWrapupForSid = useCallback((sid: string | null) => {
    if (!sid || !wrapupEnabledRef.current) return;
    const pending = wrapupRef.current;
    if (sid === pending?.call_sid || wrapupOpenAttemptedSidRef.current.has(sid)) return;
    wrapupOpenAttemptedSidRef.current.add(sid);
    openCallWrapup(sid)
      .then((res) => {
        if (!res?.data?.applicable || !res.data.wrapup) return;
        const w = res.data.wrapup;
        // Another wrap-up is expanded (the CSR switched to it mid-call): this
        // one joins the strip, taking over the notes typed during the call.
        if (wrapupRef.current) {
          const liveDraft = editorDraftsRef.current.get(`call:${sid}`);
          if (liveDraft) {
            editorDraftsRef.current.delete(`call:${sid}`);
            editorDraftsRef.current.set(`wrapup:${w.id}`, { ...liveDraft, outcomeCode: undefined, callbackDue: null });
          }
          setWrapupQueue((q) => (q.some((x) => x.id === w.id) ? q : [w, ...q]));
          return;
        }
        enterWrapupMode(w, { prefillNotes: false });
      })
      .catch((e) => console.warn("[CallWrapup] open failed", e));
  }, [enterWrapupMode]);

  // Remote hangup path: when an answered INBOUND call ends while the SDK call
  // object is still in state (the customer hung up, or the carrier dropped
  // it). The CSR's own hangup button clears activeCall synchronously, so it
  // cannot rely on this effect — handleHangup requests the wrap-up itself
  // before tearing the call down. Outbound calls, never-answered calls (no
  // activeCall) and disabled tenants are skipped.
  useEffect(() => {
    if (callStatus !== "ended" || !wrapupEnabledRef.current) return;
    if (callDirection !== "incoming" || !activeCall) return;
    requestWrapupForSid(wrapupSidForCall(activeCall));
  }, [callStatus, callDirection, activeCall, requestWrapupForSid, wrapupSidForCall]);

  // Focus input when dialpad tab is active
  useEffect(() => {
    if (expanded && activeTab === "dialpad" && !hasActiveInteraction) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [expanded, activeTab, hasActiveInteraction]);

  // ---- Handlers ----
  const fmtTimer = (s: number) =>
    `${Math.floor(s / 60)
      .toString()
      .padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
  // Wrap-up countdown chip: "0:42" (no zero-padded minutes).
  const fmtWrapupTimer = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  const handleAccept = async (call: Call) => {
    // Local-write-first: the click is evidence of the CSR's action even if
    // anything after it (auto-hold, accept, the assistant notify) fails.
    recordCallActivity({
      event_type: "accept_click",
      call_sid: call.parameters?.CallSid,
      customer_phone: call.customParameters?.get("customerPhone") || call.parameters?.From,
      call_source: call.customParameters?.get("callSource"),
    });
    // If there is already an active call, move it to private hold automatically
    // before accepting the new incoming call so no conversation is dropped.
    if (activeCall) {
      const callSid = activeCall.parameters?.CallSid;
      if (callSid) {
        setHoldLoading("private");
        try {
          const res = await request("/api/tenant/appointment-center/calls/hold", {
            method: "POST",
            withToken: true,
            data: { call_sid: callSid, type: "private", csr_id: csrId },
          });
          const customerCallSid: string | undefined = (res as any)?.callSid ?? undefined;
          moveToHeld(activeCall, "private", customerCallSid);
          setCallStatus(null);
          message.success("Previous call placed on private hold.");
        } catch (error) {
          recordCallActivity({
            event_type: "notify_failed",
            call_sid: callSid,
            details: { stage: "accept_auto_hold", hold_type: "private", error: String((error as any)?.message || error) },
          });
          message.error("Failed to hold the current call. Cannot accept new call.");
          setHoldLoading(null);
          return;
        } finally {
          setHoldLoading(null);
        }
      }
    }
    acceptIncomingCall(call);
    notifyAssistantAction("accepted", call);
  };
  const handleReject = (call: Call) => {
    recordCallActivity({
      event_type: "reject_click",
      call_sid: call.parameters?.CallSid,
      customer_phone: call.customParameters?.get("customerPhone") || call.parameters?.From,
      call_source: call.customParameters?.get("callSource"),
    });
    rejectIncomingCall(call);
    notifyAssistantAction("rejected", call);
  };
  const handleHangup = () => {
    recordCallActivity({
      event_type: "hangup_click",
      call_sid: activeCall?.parameters?.CallSid,
      customer_phone: activeCall?.customParameters?.get("customerPhone") || activeCall?.parameters?.From,
      call_source: activeCall?.customParameters?.get("callSource"),
    });
    // Wrap-up for a CSR-initiated hangup. hangupCall() nulls activeCall
    // synchronously and the lifecycle effect drops its listeners with it, so
    // the "ended" effect never sees this call — capture the SID and ask now,
    // while the call object is still here. Same rule as the remote path:
    // answered inbound calls only; the backend decides applicability.
    // (Confirmed with Zach 2026-09-21: wrap-up applies whichever side hangs up.)
    if (callDirection === "incoming" && callStatus === "in-progress") {
      requestWrapupForSid(wrapupSidForCall(activeCall));
    }
    hangupCall();
    notifyAssistantAction("hangup");
  };

  const toggleMute = () => {
    // While a call is on hold, activeCall is null — the live WebRTC leg lives
    // inside heldCalls.  Fall back to the first held call so the CSR can still
    // mute their mic (e.g. to speak internally before resuming).
    const targetCall = activeCall ?? heldCalls[0]?.call ?? null;
    if (targetCall) {
      const m = !muted;
      targetCall.mute(m);
      setMuted(m);
    }
  };

  const holdCall = async (type: "public" | "private") => {
    if (!activeCall) {
      message.warning("No active call to put on hold.");
      return;
    }
    const callSid = activeCall.parameters?.CallSid;
    if (!callSid) return;
    recordCallActivity({
      event_type: "hold_click",
      call_sid: callSid,
      customer_phone: activeCall.customParameters?.get("customerPhone") || activeCall.parameters?.From,
      call_source: activeCall.customParameters?.get("callSource"),
      details: { hold_type: type },
    });
    setHoldLoading(type);
    try {
      const res = await request("/api/tenant/appointment-center/calls/hold", {
        method: "POST",
        withToken: true,
        data: type === "private"
          ? { call_sid: callSid, type, csr_id: csrId }
          : { call_sid: callSid, type },
      });
      message.success(`Call placed on ${type} hold.`);
      // For public holds the upstream response includes the customer-leg callSid
      // (distinct from the browser/CSR-leg SID). Store it so that resumeCall can
      // send the correct SID required by the publicTakeover resume API contract.
      const customerCallSid: string | undefined = (res as any)?.callSid ?? undefined;
      // Move the call out of activeCall into the held list.
      // The browser WebRTC connection stays alive inside HeldCallInfo.
      moveToHeld(activeCall, type, customerCallSid);
      setCallStatus(null);
    } catch (error) {
      recordCallActivity({
        event_type: "notify_failed",
        call_sid: callSid,
        details: { stage: "hold", hold_type: type, error: String((error as any)?.message || error) },
      });
      message.error(`Failed to place call on ${type} hold.`);
    } finally {
      setHoldLoading(null);
    }
  };

  const handleDialPress = (digit: string) => {
    if (activeCall && callStatus === "in-progress") activeCall.sendDigits(digit);
    setDialNumber((p) => p + digit);
  };
  const handleDialBackspace = () => setDialNumber((p) => p.slice(0, -1));

  const handleDialCall = () => {
    if (!dialNumber.trim() || !twilioReady) {
      if (!twilioReady) message.error("Phone service not connected");
      return;
    }
    const numberToCall =
      IS_SANDBOX && !dialNumber.startsWith("+")
        ? dialCountryCode + dialNumber.replace(/\D/g, "")
        : dialNumber;
    if (myPhoneNo && numberToCall.replace(/\D/g, "") === myPhoneNo.replace(/\D/g, "")) {
      message.warning("You cannot call your own number.");
      return;
    }
    twilioMakeCall(numberToCall);
  };

  const callNumber = (num: string, queueId?: number) => {
    if (!twilioReady) {
      message.error("Phone service not connected");
      return;
    }
    if (myPhoneNo && num.replace(/\D/g, "") === myPhoneNo.replace(/\D/g, "")) {
      message.warning("You cannot call your own number.");
      return;
    }
    setDialNumber(num);
    setActiveTab("dialpad");
    twilioMakeCall(num, queueId);
  };

  const openSmsChat = useCallback(async (phone: string, name?: string, text?: string) => {
    setSmsTarget({ phone, name });
    setSmsChatText(text ?? "");
    setSmsChatAttachments([]);
    setSmsTemplateMediaUrls([]);
    setSmsSelectedTemplateId(undefined);
    setSmsChatMessages([]);
    setSmsChatLoading(true);
    if (!smsTemplatesLoadedRef.current) {
      smsTemplatesLoadedRef.current = true;
      getAllSmsTemplates()
        .then((res) => {
          if (res?.data) setSmsTemplates((res.data as API.SmsTemplate[]).filter((t) => t.is_active !== false));
        })
        .catch(() => { smsTemplatesLoadedRef.current = false; });
    }
    try {
      const res = await getTenantConversation(phone);
      setSmsChatMessages(res?.messages || []);
    } catch {
      setSmsChatMessages([]);
    } finally {
      setSmsChatLoading(false);
    }
  }, []);

  // Auto-grow the SMS textarea to fit its content (capped, then scrolls) —
  // keyed on the text itself rather than the textarea's onChange so it also
  // resizes when a template picker or a reset (post-send, chat reopen) sets
  // the value programmatically, not just on user keystrokes.
  useEffect(() => {
    const el = smsChatTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [smsChatText]);

  // External trigger: open widget with a phone number (dial or SMS). `text`
  // pre-fills the SMS composer, e.g. a proposal share link ready to send.
  useEffect(() => {
    const handler = (e: Event) => {
      const { phone, name, mode, text } = (e as CustomEvent<{ phone: string; name?: string; mode?: "dial" | "sms"; text?: string }>).detail;
      setExpanded(true);
      setActiveTab("dialpad");
      if (mode === "sms") {
        openSmsChat(phone, name, text);
      } else {
        setDialNumber(phone);
      }
    };
    window.addEventListener("openCallWidget", handler);
    return () => window.removeEventListener("openCallWidget", handler);
  }, [openSmsChat]);

  const sendSmsChat = async () => {
    if ((!smsChatText.trim() && smsChatAttachments.length === 0 && smsTemplateMediaUrls.length === 0) || !smsTarget) return;
    setSmsChatSending(true);
    try {
      const res = await sendTenantMessage({
        phone: smsTarget.phone,
        message: smsChatText,
        files: smsChatAttachments.length > 0 ? smsChatAttachments : undefined,
        templateMediaUrls: smsTemplateMediaUrls.length > 0 ? smsTemplateMediaUrls : undefined,
        templateId: smsSelectedTemplateId,
      });
      if (res?.success) {
        setSmsChatText("");
        setSmsChatAttachments([]);
        setSmsTemplateMediaUrls([]);
        setSmsSelectedTemplateId(undefined);
        const refreshed = await getTenantConversation(smsTarget.phone);
        setSmsChatMessages(refreshed?.messages || []);
      }
    } catch {
      // silent — keep widget unobtrusive
    } finally {
      setSmsChatSending(false);
    }
  };

  // Live updates over WebSocket — reload the open SMS thread when a push
  // concerns it: either the currently selected contact's number, or our own
  // Twilio line (see SMS_THREAD_UPDATED / call-board.ts). A payload-less push
  // (socket reconnect) also resyncs since a message may have been missed.
  useEffect(() => {
    if (!smsTarget) return;
    return onSmsThreadUpdated((payload) => {
      if (payload) {
        const matchesSelected = payload.phone_number === smsTarget.phone;
        const matchesMyNumber =
          !!myPhoneNo &&
          (payload.message.from_number === myPhoneNo || payload.message.to_number === myPhoneNo);
        if (!matchesSelected && !matchesMyNumber) return;
      }
      getTenantConversation(smsTarget.phone)
        .then((res) => setSmsChatMessages(res?.messages || []))
        .catch(() => {});
    });
  }, [smsTarget, myPhoneNo]);

  useEffect(() => {
    if (!smsChatLoading) {
      setTimeout(() => smsChatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  }, [smsChatMessages, smsChatLoading]);

  useEffect(() => {
    const urls = smsChatAttachments.map((f) =>
      f.type.startsWith("image/") ? URL.createObjectURL(f) : ""
    );
    setSmsChatPreviews(urls);
    return () => { urls.forEach((u) => { if (u) URL.revokeObjectURL(u); }); };
  }, [smsChatAttachments]);

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && dialNumber.trim()) handleDialCall();
    if (e.key === "Backspace" && dialNumber === "") e.preventDefault();
  };

  const formatPhoneDisplay = (raw: string): string => {
    if (!raw) return "";
    // DTMF codes — don't format
    if (/[*#]/.test(raw)) return raw;
    const hasPlus = raw.startsWith("+");
    const digits = raw.replace(/\D/g, "");
    const len = digits.length;
    if (len === 0) return hasPlus ? "+" : "";
    // India +91 format (sandbox only): 12 digits starting with 91
    if (IS_SANDBOX && hasPlus && digits.startsWith("91") && len >= 2) {
      const local = digits.slice(2);
      const localLen = local.length;
      if (localLen === 0) return "+91";
      if (localLen <= 5) return `+91 ${local}`;
      return `+91 ${local.slice(0, 5)} ${local.slice(5, 10)}`;
    }
    // +1 North American format (11 digits starting with 1)
    if ((hasPlus || len === 11) && digits[0] === "1" && len >= 1) {
      const area = digits.slice(1, 4);
      const prefix = digits.slice(4, 7);
      const line = digits.slice(7, 11);
      if (len <= 1) return "+1";
      if (len <= 4) return `+1 (${area}`;
      if (len <= 7) return `+1 (${area}) ${prefix}`;
      return `+1 (${area}) ${prefix}-${line}`;
    }
    // North American 10-digit
    if (len <= 3) return digits;
    if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (len <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    // Longer — show raw digits grouped
    return `+${digits}`;
  };

  const handleTopbarMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    const phoneEl = phoneRef.current;
    if (!phoneEl) return;
    const rect = phoneEl.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: rect.left, startPosY: rect.top };
    setIsDragging(true);
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setPanelPos({
        x: Math.max(0, Math.min(window.innerWidth - 80, dragRef.current.startPosX + ev.clientX - dragRef.current.startX)),
        y: Math.max(0, Math.min(window.innerHeight - 60, dragRef.current.startPosY + ev.clientY - dragRef.current.startY)),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      setIsDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip formatting, keep only raw phone chars
    const raw = e.target.value.replace(/[^0-9+*#]/g, "");
    setDialNumber(raw);
  };

  const fmtDuration = (sec: number | null) => {
    if (!sec) return "0:00";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const showAvailability = access.canToggleCallAvailability && twilioReady;
  const statusColor = showAvailability && !isCallAvailable ? "#fa8c16" : twilioReady ? "#52c41a" : twilioConnecting ? "#1890ff" : "#ff4d4f";
  const statusText = showAvailability && !isCallAvailable ? "Connected – Unavailable" : twilioReady ? "Connected" : twilioConnecting ? "Connecting..." : "Disconnected";

  // ---- Caller info popup helpers ----
  // The standalone caller-info widget covers the pre-answer window (ringing)
  // and any answered call where the notes popup is not already showing this
  // same customer's job details — once the merged notes popup has them, the
  // standalone copy hides to avoid duplication.
  // const showCallerPopup = expanded && !!customerInfo; // DEV TEST
  const showCallerPopup =
    expanded &&
    hasActiveInteraction &&
    !!customerInfo &&
    !(notesPopupOpen && notesJobInfo?.customer?.id === customerInfo.id);

  // Call-notes popup: side widget opened when an outgoing call is answered.
  // Deliberately NOT tied to the call/phone-panel state — it survives the call
  // ending (and the phone being minimized) so the user can finish the note,
  // and only disappears when they close it.
  const showNotesPopup = notesPopupOpen;

  const callerPopupStyle = React.useMemo((): React.CSSProperties => {
    const phoneWidth = fullSize ? 380 : 300;
    const popupWidth = 264;
    if (panelPos) {
      return {
        position: "fixed",
        top: panelPos.y,
        left: Math.max(8, panelPos.x - popupWidth - 12),
      };
    }
    return dockBesideStyle(dockSlotInset(0) + phoneWidth + 12);
  }, [panelPos, fullSize, dockBesideStyle]);

  // Notes popup: independently movable. Until the user drags it, it sits to
  // the left of the phone (shifted further left when the standalone caller
  // column is also visible); afterwards it stays wherever it was dropped.
  const NOTES_POPUP_WIDTH = 380;
  const notesPopupStyle = React.useMemo((): React.CSSProperties => {
    if (notesPopupPos) {
      return { position: "fixed", top: notesPopupPos.y, left: notesPopupPos.x };
    }
    const phoneWidth = fullSize ? 380 : 300;
    const callerColumnOffset = showCallerPopup ? 264 + 12 : 0;
    if (panelPos) {
      return {
        position: "fixed",
        top: panelPos.y,
        left: Math.max(8, panelPos.x - callerColumnOffset - NOTES_POPUP_WIDTH - 12),
      };
    }
    return dockBesideStyle(dockSlotInset(0) + phoneWidth + 12 + callerColumnOffset);
  }, [notesPopupPos, panelPos, fullSize, showCallerPopup, dockBesideStyle]);

  const handleNotesHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button, textarea")) return;
    e.preventDefault();
    const popupEl = notesPopupRef.current;
    if (!popupEl) return;
    const rect = popupEl.getBoundingClientRect();
    notesDragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: rect.left, startPosY: rect.top };
    setNotesDragging(true);
    const onMove = (ev: MouseEvent) => {
      if (!notesDragRef.current) return;
      setNotesPopupPos({
        x: Math.max(0, Math.min(window.innerWidth - 80, notesDragRef.current.startPosX + ev.clientX - notesDragRef.current.startX)),
        y: Math.max(0, Math.min(window.innerHeight - 60, notesDragRef.current.startPosY + ev.clientY - notesDragRef.current.startY)),
      });
    };
    const onUp = () => {
      notesDragRef.current = null;
      setNotesDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const fmtTime12 = (t?: string) => {
    if (!t) return "";
    const [h, m] = t.split(":");
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  const fmtApptDate = (d?: string) => {
    if (!d) return "";
    const [y, mo, day] = d.split("-");
    return `${parseInt(mo, 10)}/${parseInt(day, 10)}/${y}`;
  };

  // Job-details rows shared by the standalone caller-info widget and the
  // job section inside the merged call-notes popup.
  const renderJobInfoBody = (job: API.Dispatch) => {
    const appt = job.dispatch_appointment;
    const techNames: string[] = ((job as any)?.technicians as any[] | undefined)
      ?.map((u: any) => u.user_name)
      .filter(Boolean) || [];
    const address = job.address;
    const addressStr =
      address?.full_address ||
      [address?.streetNumber, address?.streetName, address?.city, address?.state, address?.zip]
        .filter(Boolean)
        .join(" ");

    return (
      <div className="cw-caller-info-popup__body">
            {/* Job ID */}
            <div className="cw-info-row">
              <span className="cw-info-label">Job</span>
              <span className="cw-info-value">
                <Typography.Text
                  copyable={{ text: String(job.id) }}
                  className="cw-info-link"
                  onClick={() => { window.open(`${PUBLIC_PATH}tenant/dashboard/dispatch-detail/${job.id}`, '_blank'); }}
                >
                  #{job.id}
                </Typography.Text>
              </span>
            </div>

            {/* Status */}
            {job.dispatch_status && (() => {
              const label = getStatusDescription(job);
              const code = job.dispatch_status.job_status ?? '';
              const color = DispatchColors.job_status?.[code];
              return (
                <div className="cw-info-row">
                  <span className="cw-info-label">Status</span>
                  <span className="cw-info-value">
                    <Tag style={{ borderRadius: 4, marginBottom: 0, ...(color ? { color: '#fff', background: color, borderColor: color } : {}) }}>
                      {label}
                    </Tag>
                  </span>
                </div>
              );
            })()}

            {/* External ID */}
            {job.external_id != null && (
              <div className="cw-info-row">
                <span className="cw-info-label">Ext ID</span>
                <span className="cw-info-value">
                  <Typography.Text copyable={{ text: String(job.external_id) }}>
                    {job.external_id}
                  </Typography.Text>
                </span>
              </div>
            )}

            {/* Follow-up info */}
            {job.visit_type === 'follow_up' && (
              <>
                {job.follow_up_number != null && (
                  <div className="cw-info-row">
                    <span className="cw-info-label">Follow Up #</span>
                    <span className="cw-info-value">{job.follow_up_number}</span>
                  </div>
                )}
                {(job.mainDispatch?.id || job.main_dispatch_id) && (
                  <div className="cw-info-row">
                    <span className="cw-info-label">Main Job</span>
                    <span className="cw-info-value">
                      <Typography.Text
                        className="cw-info-link"
                        onClick={() => { window.open(`${PUBLIC_PATH}tenant/dashboard/dispatch-detail/${job.mainDispatch?.id ?? job.main_dispatch_id}`, '_blank'); }}
                      >
                        #{job.mainDispatch?.id ?? job.main_dispatch_id}
                      </Typography.Text>
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Priority */}
            {(job.priority_term?.name || job.priority) && (
              <div className="cw-info-row">
                <span className="cw-info-label">Priority</span>
                <span className="cw-info-value">
                  <Tag color={job.priority_term?.color ?? "default"} style={{ borderRadius: 4, marginBottom: 0 }}>
                    {job.priority_term?.name || job.priority}
                  </Tag>
                </span>
              </div>
            )}

            {/* Tags */}
            {job.tags && job.tags.length > 0 && (
              <div className="cw-info-row cw-info-row--wrap">
                <span className="cw-info-label">Tags</span>
                <span className="cw-info-value cw-info-value--wrap">
                  <TagDisplay tags={job.tags} size="small" />
                </span>
              </div>
            )}

            {/* Service Types */}
            {job.service_types && job.service_types.length > 0 && (
              <div className="cw-info-row cw-info-row--wrap">
                <span className="cw-info-label">Service</span>
                <span className="cw-info-value cw-info-value--wrap">
                  <TagDisplay tags={job.service_types} size="small" />
                </span>
              </div>
            )}

            {/* Appointment */}
            {appt?.appointment_date && (
              <div className="cw-info-row">
                <span className="cw-info-label">Appt</span>
                <span className="cw-info-value">
                  <CalendarOutlined style={{ marginRight: 4, opacity: 0.6 }} />
                  {fmtApptDate(appt.appointment_date)}
                  {(appt.time_from || appt.appointment_time) &&
                    ` · ${fmtTime12(appt.time_from || appt.appointment_time!)}${appt.time_to ? ` – ${fmtTime12(appt.time_to)}` : ""}`}
                </span>
              </div>
            )}

            {/* Technician(s) */}
            {techNames.length > 0 && (
              <div className="cw-info-row">
                <span className="cw-info-label">Tech</span>
                <span className="cw-info-value">
                  <UserOutlined style={{ marginRight: 4, opacity: 0.6 }} />
                  {techNames.join(", ")}
                </span>
              </div>
            )}

            {/* Address */}
            {!!addressStr && (
              <div className="cw-info-row cw-info-row--address">
                <span className="cw-info-label">Addr</span>
                <span className="cw-info-value">
                  <EnvironmentOutlined style={{ marginRight: 4, opacity: 0.6 }} />
                  <Typography.Text copyable={{ text: addressStr }}>{addressStr}</Typography.Text>
                </span>
              </div>
            )}
      </div>
    );
  };

  const renderCallerInfoPopup = () => {
    if (!showCallerPopup) return null;
    const job = callerJobInfo;
    return (
      <div className="cw-caller-info-popup">
        <div className="cw-caller-info-popup__header">
          <UserOutlined />
          <span>{customerInfo?.name || "Customer"}</span>
          {callerJobLoading && <Spin size="small" />}
        </div>
        {job
          ? renderJobInfoBody(job)
          : !callerJobLoading && (
              <div className="cw-caller-info-popup__empty">No recent jobs found</div>
            )}
      </div>
    );
  };

  const renderCallNotesPopup = () => {
    if (!showNotesPopup) return null;
    return (
      <div
        ref={notesPopupRef}
        className={`cw-caller-info-popup cw-notes-popup${notesJobInfo?.job ? " cw-notes-popup--with-job" : ""}${wrapup ? " cw-notes-popup--wrapup" : ""}${notesDragging ? " cw-notes-popup--dragging" : ""}${notesMinimized ? " cw-notes-popup--minimized" : ""}`}
        style={notesPopupStyle}
      >
        <div className="cw-caller-info-popup__header cw-notes-popup__header" onMouseDown={handleNotesHeaderMouseDown}>
          <FileTextOutlined />
          <span
            onClick={notesMinimized ? () => setNotesMinimized(false) : undefined}
            style={notesMinimized ? { cursor: "pointer" } : undefined}
            title={notesMinimized ? "Click to expand" : undefined}
          >
            {wrapup ? "Call Wrap-up" : "Call Notes"}
          </span>
          {wrapupQueue.length > 0 && (
            <span
              className="cw-notes-popup__queue-chip"
              title={`${wrapupQueue.length} more wrap-up${wrapupQueue.length === 1 ? "" : "s"} waiting`}
              onClick={notesMinimized ? () => setNotesMinimized(false) : undefined}
              style={notesMinimized ? { cursor: "pointer" } : undefined}
            >
              +{wrapupQueue.length} waiting
            </span>
          )}
          {wrapup && (
            wrapupRemaining > 0 ? (
              <span className="cw-notes-popup__wrapup-timer">Wrap-up {fmtWrapupTimer(wrapupRemaining)}</span>
            ) : (
              <span className="cw-notes-popup__wrapup-timer cw-notes-popup__wrapup-timer--warning">
                Wrap-up time over — save your outcome
              </span>
            )
          )}
          <span className={`cw-notes-popup__status cw-notes-popup__status--${notesSaveState}`}>
            {notesSaveState === "saving"
              ? "Saving…"
              : notesSaveState === "saved"
                ? "Saved"
                : notesSaveState === "error"
                  ? "Save failed — retrying"
                  : notesSaveState === "failed"
                    ? "Save failed"
                    : "Autosaves as you type"}
          </span>
          <Tooltip title={notesMinimized ? "Expand" : "Minimize"} placement="bottom">
            <button
              type="button"
              className="cw-notes-popup__close cw-notes-popup__minimize"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => setNotesMinimized((m) => !m)}
              aria-label={notesMinimized ? "Expand" : "Minimize"}
            >
              {notesMinimized ? <ExpandOutlined /> : <MinusOutlined />}
            </button>
          </Tooltip>
          <Tooltip title="Close" placement="bottom">
            <button type="button" className="cw-notes-popup__close" onClick={wrapup ? closeWrapupPopup : closeNotesPopup}>
              <CloseOutlined />
            </button>
          </Tooltip>
        </div>
        {/* Minimized: only the header bar above stays (timer and status keep
            updating); everything below is unmounted until expanded. The
            notes draft lives in state, so nothing typed is lost. */}
        {!notesMinimized && (<>
        {/* Collapsed strip: every other wrap-up this agent still owes. One row
            each; click expands it (the current editor is parked with its
            draft). Overdue = the server already listed it as a missing
            wrap-up; a late save still works and clears that. */}
        {wrapupQueue.length > 0 && (
          <div className="cw-notes-popup__queue">
            <div className="cw-notes-popup__queue-title">
              {wrapup ? "Other wrap-ups waiting" : "Wrap-ups waiting from previous calls"}
            </div>
            {wrapupQueue.slice(0, WRAPUP_QUEUE_MAX).map((q) => (
              <button
                type="button"
                key={q.id}
                className={`cw-notes-popup__queue-row${q.status === "expired" ? " cw-notes-popup__queue-row--overdue" : ""}`}
                onClick={() => switchToWrapup(q)}
                title="Open this wrap-up"
              >
                <span className="cw-notes-popup__queue-phone">
                  {q.customer_phone ? formatPhoneDisplay(q.customer_phone) : "Unknown caller"}
                </span>
                <span className="cw-notes-popup__queue-meta">
                  {q.dispatch_id ? `#${q.dispatch_id} · ` : ""}ended {dtFormat(q.call_ended_at, "h:mm A")}
                </span>
                {q.status === "expired" && <span className="cw-notes-popup__queue-badge">Overdue</span>}
                <RightOutlined className="cw-notes-popup__queue-arrow" />
              </button>
            ))}
            {wrapupQueue.length > WRAPUP_QUEUE_MAX && (
              <div className="cw-notes-popup__queue-more">
                and {wrapupQueue.length - WRAPUP_QUEUE_MAX} more — see Admin → Call Follow-ups
              </div>
            )}
          </div>
        )}
        {/* Matched-job section — shown only when the caller's number resolved
            to a customer with a recent job. Reads the snapshot (not the live
            call state) so it stays visible while finishing the note after
            the call ends. */}
        {notesJobInfo?.job && (
          <div className="cw-notes-popup__job">
            <div className="cw-notes-popup__job-header">
              <UserOutlined />
              <span>{notesJobInfo.customer?.name || "Customer"}</span>
            </div>
            {renderJobInfoBody(notesJobInfo.job)}
          </div>
        )}
        {/* Wrap-up controls — outcome (required), optional callback due /
            escalation hint. The notes textarea below is shared with plain
            notes mode and keeps its autosave; its text is sent as the
            wrap-up notes on save. */}
        {wrapup && (
          <div className="cw-notes-popup__wrapup">
            <div className="cw-notes-popup__wrapup-row">
              <span className="cw-notes-popup__wrapup-label">Outcome</span>
              <Select
                size="small"
                className="cw-template-select cw-notes-popup__outcome"
                popupClassName="cw-template-select-dropdown"
                placeholder="Select an outcome…"
                style={{ flex: 1 }}
                value={wrapupOutcomeCode}
                onChange={handleWrapupOutcomeChange}
                disabled={wrapupSaving || !!wrapupSavedLabel}
                optionLabelProp="label"
                options={voiceOutcomes.map((o) => ({
                  value: o.code,
                  label: (
                    <span className="cw-notes-popup__outcome-option">
                      <span className="cw-notes-popup__outcome-dot" style={{ background: o.color || "rgba(255,255,255,0.35)" }} />
                      {o.label}
                    </span>
                  ),
                }))}
              />
            </div>
            {selectedWrapupOutcome?.requires_notes && (
              <div className={`cw-notes-popup__wrapup-hint${wrapupNotesMissing ? " cw-notes-popup__wrapup-hint--required" : ""}`}>
                Notes required
              </div>
            )}
            {selectedWrapupOutcome?.next_action_type === "callback" && (
              <div className="cw-notes-popup__wrapup-row">
                <span className="cw-notes-popup__wrapup-label">Callback due</span>
                <DatePicker
                  size="small"
                  className="cw-notes-popup__callback-due"
                  style={{ flex: 1 }}
                  showTime={{ format: "HH:mm" }}
                  format="MM/DD/YYYY HH:mm"
                  allowClear={false}
                  value={wrapupCallbackDue}
                  onChange={(v) => setWrapupCallbackDue(v)}
                  disabled={wrapupSaving || !!wrapupSavedLabel}
                />
              </div>
            )}
            {/* Quick windows — "I told the customer I'd call back within 30 minutes"
                (Zach). Sets the due time; the bell reminds the agent when it passes. */}
            {selectedWrapupOutcome?.next_action_type === "callback" && (
              <div className="cw-notes-popup__due-presets">
                {([15, 30, 60, 120] as const).map((mins) => {
                  const active = !!wrapupCallbackDue && Math.abs(wrapupCallbackDue.diff(dayjs(), "minute") - mins) <= 1;
                  return (
                    <button
                      key={mins}
                      type="button"
                      className={`cw-notes-popup__due-preset${active ? " cw-notes-popup__due-preset--active" : ""}`}
                      disabled={wrapupSaving || !!wrapupSavedLabel}
                      onClick={() => setWrapupCallbackDue(dayjs().add(mins, "minute"))}
                    >
                      {mins < 60 ? `${mins} min` : `${mins / 60} h`}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="cw-notes-popup__due-preset"
                  disabled={wrapupSaving || !!wrapupSavedLabel}
                  onClick={() => setWrapupCallbackDue(dayjs().add(1, "day").hour(9).minute(0).second(0))}
                >
                  Tomorrow 9 AM
                </button>
                <span className="cw-notes-popup__due-hint">Reminder in the bell when due</span>
              </div>
            )}
            {selectedWrapupOutcome?.next_action_type === "escalation" && (
              <div className="cw-notes-popup__wrapup-hint">An escalation will be created for a manager.</div>
            )}
            {/* Replay the call before choosing the outcome — shown once the
                recording exists (not yet for a call that ended seconds ago). */}
            {wrapup.has_recording && wrapup.twilio_call_log_id && (
              <WrapupRecordingPlayer logId={wrapup.twilio_call_log_id} />
            )}
          </div>
        )}
        <div className="cw-notes-popup__body">
          <textarea
            className="cw-notes-popup__input"
            placeholder={wrapup ? "Wrap-up notes for this call..." : "Type notes about this call..."}
            value={callNotes}
            onChange={(e) => handleNotesChange(e.target.value)}
            rows={wrapup ? (notesJobInfo?.job ? 5 : 8) : notesJobInfo?.job ? 7 : 12}
          />
        </div>
        <div className="cw-notes-popup__footer">
          {wrapup ? (
            <>
              {wrapupError && <span className="cw-notes-popup__error">{wrapupError}</span>}
              <button
                type="button"
                className={`cw-notes-popup__save${wrapupSavedLabel ? " cw-notes-popup__save--done" : ""}`}
                onClick={handleWrapupSave}
                disabled={!canSaveWrapup}
              >
                <SaveOutlined />
                {wrapupSavedLabel ? `Saved · ${wrapupSavedLabel}` : wrapupSaving ? "Saving…" : "Save wrap-up"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="cw-notes-popup__save"
              onClick={() => {
                // Manual save restarts the retry budget after "failed".
                notesRetryCountRef.current = 0;
                flushCallNotes();
              }}
              disabled={notesSaveState === "saving" || callNotes === lastSavedNotesRef.current}
            >
              <SaveOutlined />
              {notesSaveState === "saving" ? "Saving…" : "Save"}
            </button>
          )}
        </div>
        </>)}
      </div>
    );
  };

  // ---- Render helpers ----

  // Name for the ringing card. customerInfo is looked up for the ringing
  // number but lingers from the previous call until that lookup resolves, so
  // the name is shown only when the fetched customer's phone is the number
  // that is ringing — otherwise the card stays number-only.
  const sameNumber = (a?: string | null, b?: string | null) => {
    const da = (a || "").replace(/\D/g, "").slice(-10);
    const db = (b || "").replace(/\D/g, "").slice(-10);
    return da.length === 10 && da === db;
  };
  const ringingCustomerName = (number: string): string | null =>
    customerInfo?.name && sameNumber(customerInfo.phone, number) ? customerInfo.name : null;

  const renderCallScreen = () => (
    <div className={`cw-call-screen ${callStatus === "ringing" && callDirection === "incoming" ? "cw-call-screen--ringing" : ""}`}>
      <div className="cw-call-screen__contact">
        <div className="cw-call-screen__avatar">
          <UserOutlined />
        </div>
        {activeCall ? (
          <>
            <div className="cw-call-screen__name">{customerInfo?.name || "Unknown"}</div>
            <div className="cw-call-screen__number">{phoneNumber || "N/A"}</div>
            {currentCall?.customParameters?.get('callSource') === 'agent' && (
              <div className="cw-call-screen__agent-badge" style={{paddingLeft: 4}}>via AI Agent</div>
            )}
            <div className="cw-call-screen__status">
              {callStatus === "ended"
                ? callEndReason || "Call ended"
                : callStatus === "in-progress"
                  ? fmtTimer(callTimer)
                  : callDirection === "incoming"
                    ? "Incoming call..."
                    : "Calling..."}
            </div>
            {/* Wrap-ups from previous calls are parked in the notes popup's
                strip while this call is live — remind the CSR they exist. */}
            {callStatus !== "ended" && wrapupQueue.length + (wrapup ? 1 : 0) > 0 && (
              <div className="cw-call-screen__wrapup-pending">
                {wrapupQueue.length + (wrapup ? 1 : 0) === 1
                  ? "1 wrap-up waiting from a previous call"
                  : `${wrapupQueue.length + (wrapup ? 1 : 0)} wrap-ups waiting from previous calls`}
              </div>
            )}
          </>
        ) : (
          <div className="cw-call-screen__name cw-call-screen__name--idle">
            {incomingCalls.length > 0 ? "Incoming call..." : "No active call"}
          </div>
        )}
      </div>
      {/* Held-call list intentionally hidden from the widget.
          Resume is handled exclusively from the Hold Queue panel on the call board. */}
      {/* Call-waiting list: shown when new calls arrive while on a call or holding */}
      {incomingCalls.length > 0 && activeCall && (
        <div className="cw-call-waiting-list">
          {incomingCalls.map((call, idx) => (
            <div key={call.parameters?.CallSid || idx} className="cw-call-waiting-item">
              <span className="cw-call-waiting-item__label">
                <PhoneFilled className="cw-call-waiting-item__icon" />
                {call.customParameters?.get('customerPhone') || call.parameters?.From || "Unknown"}
                {call.customParameters?.get('callSource') === 'agent' && (
                  <span className="cw-agent-badge">AI</span>
                )}
              </span>
              <div className="cw-call-waiting-item__actions">
                <Tooltip title="Decline" placement="top">
                  <button type="button" className="cw-action-btn cw-action-btn--reject cw-action-btn--sm cw-action-btn--icon-only" onClick={() => handleReject(call)}>
                    <PhoneOutlined style={{ transform: "rotate(135deg)" }} />
                  </button>
                </Tooltip>
                <Tooltip title="Accept &amp; Hold current" placement="top">
                  <button type="button" className="cw-action-btn cw-action-btn--accept cw-action-btn--sm cw-action-btn--icon-only" onClick={() => handleAccept(call)}>
                    <PhoneOutlined />
                  </button>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="cw-call-screen__actions">
        {callStatus === "ended" ? (
          <button
            type="button"
            className="cw-action-btn cw-action-btn--neutral"
            onClick={() => {
              setCallStatus(null);
              clearActiveCall();
            }}
          >
            <CloseOutlined />
            <span>Close</span>
          </button>
        ) : incomingCalls.length > 0 && !activeCall ? (
          <div className="cw-incoming-call-list">
            {incomingCalls.map((call, idx) => {
              const number = call.customParameters?.get('customerPhone') || call.parameters?.From || "Unknown";
              const name = ringingCustomerName(number);
              return (
              <div key={call.parameters?.CallSid || idx} className="cw-incoming-call-item">
                {name && <span className="cw-incoming-call-item__name">{name}</span>}
                <span className="cw-incoming-call-item__number">
                  {number}
                  {call.customParameters?.get('callSource') === 'agent' && (
                    <span className="cw-agent-badge" style={{paddingLeft: 4}}>via AI Agent</span>
                  )}
                </span>
                <div className="cw-incoming-call-item__actions">
                  <button type="button" className="cw-action-btn cw-action-btn--reject" onClick={() => handleReject(call)}>
                    <PhoneOutlined style={{ transform: "rotate(135deg)" }} />
                    <span>Decline</span>
                  </button>
                  <button type="button" className="cw-action-btn cw-action-btn--accept" onClick={() => handleAccept(call)}>
                    <PhoneOutlined />
                    <span>Accept</span>
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <>
            <button type="button" className={`cw-action-btn ${muted ? "cw-action-btn--active" : ""}`} onClick={toggleMute}>
              {muted ? <AudioMutedOutlined /> : <AudioOutlined />}
              <span>{muted ? "Unmute" : "Mute"}</span>
            </button>
            {callStatus === "in-progress" && (
              <>
                <button
                  type="button"
                  className="cw-action-btn cw-action-btn--hold-private"
                  onClick={() => holdCall("private")}
                  disabled={holdLoading !== null}
                >
                  <LockOutlined />
                  <span>Priv. Hold</span>
                </button>
                <button
                  type="button"
                  className="cw-action-btn cw-action-btn--hold-public"
                  onClick={() => holdCall("public")}
                  disabled={holdLoading !== null}
                >
                  <GlobalOutlined />
                  <span>Pub. Hold</span>
                </button>
              </>
            )}
            <button type="button" className="cw-action-btn cw-action-btn--reject" onClick={handleHangup}>
              <PhoneOutlined style={{ transform: "rotate(135deg)" }} />
              <span>End</span>
            </button>
            <button
              type="button"
              className="cw-action-btn"
              onClick={() => {
                history.push("/tenant/communications/call_board");
                setExpanded(false);
              }}
            >
              <ExpandOutlined />
              <span>Full</span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  const renderSmsChat = () => (
    <div className="cw-tab-content" style={{ display: "flex", flexDirection: "column", height: "100%", padding: 0 }}>
      {/* Header */}
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid rgba(255,255,255,0.1)", flexShrink: 0 }}
      >
        <button type="button" className="cw-phone__topbar-btn" onClick={() => setSmsTarget(null)} title="Back">
          <ArrowLeftOutlined />
        </button>
        <div style={{ flex: 1, fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {smsTarget?.name || smsTarget?.phone}
        </div>
        {smsTarget?.name && smsTarget.name !== smsTarget.phone && <div style={{ fontSize: 11, opacity: 0.6, flexShrink: 0 }}>{smsTarget.phone}</div>}
        <button
          type="button"
          className="cw-phone__topbar-btn"
          title="Call"
          onClick={() => {
            callNumber(smsTarget!.phone);
            setSmsTarget(null);
          }}
        >
          <PhoneOutlined style={{ color: "#52c41a" }} />
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
        {smsChatLoading ? (
          <div style={{ textAlign: "center", paddingTop: 24 }}>
            <Spin size="small" />
          </div>
        ) : smsChatMessages.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No messages" />
        ) : (
          smsChatMessages.map((msg: any) => {
            const out = msg.direction?.startsWith("outbound");
            return (
              <div key={msg.id} style={{ marginBottom: 6, display: "flex", justifyContent: out ? "flex-end" : "flex-start" }}>
                <div
                  style={{
                    maxWidth: "80%",
                    padding: "6px 10px",
                    borderRadius: out ? "12px 12px 3px 12px" : "12px 12px 12px 3px",
                    background: out ? "#1890ff" : "rgba(255,255,255,0.15)",
                    color: "#fff",
                    fontSize: 12,
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                  }}
                >
                  <div>
                    {msg.type === "call"
                      ? `📞 ${out ? "Outgoing" : "Incoming"} Call${msg.duration ? ` · ${Math.floor(msg.duration / 60)}:${String(msg.duration % 60).padStart(2, "0")}` : ""}`
                      : msg.body || msg.content || ""}
                    {msg.type === "sms" && msg.attachments?.length > 0 && (
                      <div style={{ marginTop: (msg.body || msg.content) ? 4 : 0 }}>
                        {(() => {
                          const imgAtts = msg.attachments.filter((a: any) => a.attachment_type?.startsWith("image/"));
                          const fileAtts = msg.attachments.filter((a: any) => !a.attachment_type?.startsWith("image/"));
                          return (
                            <>
                              {imgAtts.length > 0 && (
                                <Image.PreviewGroup>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                                    {imgAtts.map((att: any) => (
                                      <Image
                                        key={att.id}
                                        src={`${API_URL}/${att.attachment_path}`}
                                        width={110}
                                        style={{ borderRadius: 4, objectFit: "cover", maxHeight: 110 }}
                                      />
                                    ))}
                                  </div>
                                </Image.PreviewGroup>
                              )}
                              {fileAtts.map((att: any) => (
                                <a
                                  key={att.id}
                                  href={`${API_URL}/${att.attachment_path}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 3, fontSize: 11, color: "rgba(255,255,255,0.85)" }}
                                >
                                  <PaperClipOutlined />
                                  <span style={{ textDecoration: "underline", wordBreak: "break-all" }}>{att.attachment_name}</span>
                                </a>
                              ))}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 10, opacity: 0.55, marginTop: 2, textAlign: "right", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4 }}>
                    <span>{fmtTime(msg.timestamp)}</span>
                    {out && msg.type === "sms" && (
                      <span style={{ opacity: 1 }}>
                        <SmsDeliveryStatusIndicator status={msg.delivery_status} />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={smsChatEndRef} />
      </div>

      {/* Input */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", flexShrink: 0 }}>
        {/* Hidden file input */}
        <input
          ref={smsChatFileRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            setSmsChatAttachments((prev) => [...prev, ...Array.from(e.target.files || [])]);
            e.target.value = "";
          }}
        />

        {/* Template picker */}
        {smsTemplates.length > 0 && (
          <div style={{ padding: "8px 10px 0" }}>
            <Select
              size="small"
              className="cw-template-select"
              popupClassName="cw-template-select-dropdown"
              placeholder="Use a template..."
              style={{ width: "100%" }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              value={undefined}
              onChange={(val) => {
                if (!val) { setSmsTemplateMediaUrls([]); setSmsSelectedTemplateId(undefined); return; }
                const tpl = smsTemplates.find((t) => t.id === val);
                if (tpl) {
                  setSmsChatText(tpl.content);
                  setSmsTemplateMediaUrls(tpl.media_urls ?? []);
                  setSmsSelectedTemplateId(tpl.id);
                }
              }}
              options={smsTemplates.map((t) => ({
                value: t.id,
                label: t.name,
              }))}
            />
          </div>
        )}

        {/* Template MMS images */}
        {smsTemplateMediaUrls.length > 0 && (
          <div style={{
            display: "flex", gap: 6, overflowX: "auto", padding: "6px 10px 2px",
          }}>
            {smsTemplateMediaUrls.map((url) => (
              <div key={url} style={{
                position: "relative", flexShrink: 0, width: 54, height: 54,
                borderRadius: 6, overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.15)",
                background: "#000",
              }}>
                <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                <button
                  type="button"
                  onClick={() => setSmsTemplateMediaUrls((prev) => prev.filter((u) => u !== url))}
                  style={{
                    position: "absolute", top: 2, right: 2,
                    width: 14, height: 14, border: "none", borderRadius: "50%",
                    background: "rgba(0,0,0,0.6)", color: "#fff",
                    fontSize: 8, cursor: "pointer", padding: 0, lineHeight: 1,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Selected files preview */}
        {smsChatAttachments.length > 0 && (
          <div style={{
            display: "flex", gap: 6, overflowX: "auto", padding: "6px 10px 2px",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}>
            {smsChatAttachments.map((file, idx) => {
              const isImg = file.type.startsWith("image/");
              const preview = smsChatPreviews[idx];
              const ext = (file.name.split(".").pop() || "FILE").toUpperCase().slice(0, 4);
              const sizeLabel = file.size < 1048576
                ? `${(file.size / 1024).toFixed(1)} KB`
                : `${(file.size / 1048576).toFixed(1)} MB`;
              return (
                <div key={idx} style={{
                  position: "relative", flexShrink: 0, width: 54, height: 54,
                  borderRadius: 6, overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: isImg ? "#000" : "rgba(255,255,255,0.08)",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                }}>
                  {isImg && preview ? (
                    <>
                      <img src={preview} alt={file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <div style={{
                        position: "absolute", bottom: 0, left: 0, right: 0,
                        background: "rgba(0,0,0,0.5)", color: "#fff",
                        fontSize: 8, padding: "1px 3px", textAlign: "center",
                      }}>{sizeLabel}</div>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", letterSpacing: 0 }}>{ext}</span>
                      <span style={{ fontSize: 8, color: "rgba(255,255,255,0.6)", marginTop: 2, padding: "0 2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%", textAlign: "center" }}>{sizeLabel}</span>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setSmsChatAttachments((prev) => prev.filter((_, i) => i !== idx))}
                    style={{
                      position: "absolute", top: 2, right: 2,
                      width: 14, height: 14, border: "none", borderRadius: "50%",
                      background: "rgba(0,0,0,0.6)", color: "#fff",
                      fontSize: 8, cursor: "pointer", padding: 0, lineHeight: 1,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >✕</button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: "flex", gap: 6, padding: "8px 10px", alignItems: "flex-end" }}>
          <textarea
            ref={smsChatTextareaRef}
            className="cw-search-input cw-sms-textarea"
            style={{ flex: 1 }}
            placeholder="Type a message..."
            rows={1}
            value={smsChatText}
            onChange={(e) => setSmsChatText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendSmsChat();
              }
            }}
            autoFocus
          />
          <button
            type="button"
            className="cw-call-btn"
            style={{ width: 36, height: 36, fontSize: 14, flexShrink: 0 }}
            onClick={() => smsChatFileRef.current?.click()}
            disabled={smsChatSending}
            title="Attach files"
          >
            <PaperClipOutlined />
          </button>
          <button
            type="button"
            className={`cw-call-btn ${(!smsChatText.trim() && smsChatAttachments.length === 0 && smsTemplateMediaUrls.length === 0) || smsChatSending ? "cw-call-btn--disabled" : ""}`}
            style={{ width: 36, height: 36, fontSize: 14, flexShrink: 0 }}
            onClick={sendSmsChat}
            disabled={(!smsChatText.trim() && smsChatAttachments.length === 0 && smsTemplateMediaUrls.length === 0) || smsChatSending}
          >
            {smsChatSending ? <Spin size="small" /> : <SendOutlined />}
          </button>
        </div>
      </div>
    </div>
  );

  const renderDialpad = () => (
    <div className="cw-tab-content">
      {IS_SANDBOX && (
        <div className="cw-country-toggle">
          <button
            type="button"
            className={`cw-country-btn${dialCountryCode === "+1" ? " cw-country-btn--active" : ""}`}
            onClick={() => setDialCountryCode("+1")}
          >
            US +1
          </button>
          <button
            type="button"
            className={`cw-country-btn${dialCountryCode === "+91" ? " cw-country-btn--active" : ""}`}
            onClick={() => setDialCountryCode("+91")}
          >
            IN +91
          </button>
        </div>
      )}
      {/* Editable number input */}
      <div className="cw-dial-display">
        <input
          ref={inputRef}
          className="cw-dial-input"
          type="tel"
          value={formatPhoneDisplay(dialNumber)}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder="Enter number"
          autoComplete="off"
        />
        {dialLookup?.name && <div className="cw-dial-lookup">{dialLookup.name}</div>}
      </div>
      <div className="cw-dialpad">
        {DIALPAD_KEYS.map((k) => (
          <button key={k.digit} type="button" className="cw-key" onClick={() => handleDialPress(k.digit)}>
            <span className="cw-key__digit">{k.digit}</span>
            {k.letters && <span className="cw-key__letters">{k.letters}</span>}
          </button>
        ))}
      </div>
      <div className="cw-dial-bottom">
        <button
          type="button"
          className="cw-dial-bottom__side"
          style={{ visibility: dialNumber.trim() ? "visible" : "hidden" }}
          onClick={() => openSmsChat(dialNumber.trim())}
          title="Send SMS"
        >
          <MessageOutlined style={{ color: "#1890ff" }} />
        </button>
        <button
          type="button"
          className={`cw-call-btn ${!twilioReady || !dialNumber ? "cw-call-btn--disabled" : ""}`}
          onClick={handleDialCall}
          disabled={!twilioReady || !dialNumber}
        >
          <PhoneOutlined />
        </button>
        <button
          type="button"
          className="cw-dial-bottom__side"
          onClick={handleDialBackspace}
          style={{ visibility: dialNumber ? "visible" : "hidden" }}
        >
          <DeleteOutlined />
        </button>
      </div>
    </div>
  );

  const renderRecents = () => (
    <div className="cw-tab-content cw-tab-content--list">
      {recentsLoading ? (
        <div className="cw-list-center">
          <Spin size="small" />
        </div>
      ) : recentCalls.length === 0 ? (
        <div className="cw-list-center">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent calls" />
        </div>
      ) : (
        <div className="cw-list">
          {recentCalls.map((c) => {
            const isOutgoing = c.direction?.startsWith("outbound");
            const num = c.contact_phone || (isOutgoing ? c.to_number : c.from_number);
            const isMissed = c.call_status === "no-answer" || c.call_status === "busy" || c.call_status === "canceled";
            return (
              <div key={c.id} className="cw-list-item" onClick={() => callNumber(num)}>
                <div className={`cw-list-item__icon ${isMissed ? "cw-list-item__icon--missed" : isOutgoing ? "cw-list-item__icon--out" : ""}`}>
                  <PhoneFilled style={{ transform: isOutgoing ? "rotate(135deg) scaleX(-1)" : undefined }} />
                </div>
                <div className="cw-list-item__info">
                  <div className="cw-list-item__primary">{num || "Unknown"}</div>
                  <div className="cw-list-item__secondary">
                    {isOutgoing ? "Outgoing" : "Incoming"} · {fmtDuration(c.duration || c.call_duration)} · {fmtTime(c.created_at)}
                  </div>
                  {c.notes && (
                    <div className="cw-list-item__note" title={c.notes}>
                      <FileTextOutlined /> {c.notes}
                    </div>
                  )}
                </div>
                <div className="cw-item-actions" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="cw-icon-btn cw-icon-btn--sms" title="Send SMS" onClick={() => openSmsChat(num)}>
                    <MessageOutlined />
                  </button>
                  <span className="cw-item-actions__sep" />
                  <button type="button" className="cw-icon-btn cw-icon-btn--call" title="Call" onClick={() => callNumber(num)}>
                    <PhoneOutlined />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderContacts = () => (
    <div className="cw-tab-content cw-tab-content--list">
      <div className="cw-search-bar">
        <input
          className="cw-search-input"
          placeholder="Search contacts..."
          value={contactSearch}
          onChange={(e) => setContactSearch(e.target.value)}
        />
      </div>
      {contactsLoading ? (
        <div className="cw-list-center">
          <Spin size="small" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="cw-list-center">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No contacts" />
        </div>
      ) : (
        <div className="cw-list">
          {contacts.map((c) => (
            <div key={c.uid} className="cw-list-item" onClick={() => callNumber(c.phone_number || c.phone)}>
              <div className="cw-list-item__icon">
                <UserOutlined />
              </div>
              <div className="cw-list-item__info">
                <div className="cw-list-item__primary">{c.name || c.phone_number || c.phone}</div>
                <div className="cw-list-item__secondary">{c.phone_number || c.phone}</div>
              </div>
              <div className="cw-item-actions" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="cw-icon-btn cw-icon-btn--sms"
                  title="Send SMS"
                  onClick={() => openSmsChat(c.phone_number || c.phone, c.name)}
                >
                  <MessageOutlined />
                </button>
                <span className="cw-item-actions__sep" />
                <button type="button" className="cw-icon-btn cw-icon-btn--call" title="Call" onClick={() => callNumber(c.phone_number || c.phone)}>
                  <PhoneOutlined />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderQueue = () => (
    <div className="cw-tab-content cw-tab-content--list">
      {queueLoading ? (
        <div className="cw-list-center">
          <Spin size="small" />
        </div>
      ) : queueItems.length === 0 ? (
        <div className="cw-list-center">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No pending callbacks" />
        </div>
      ) : (
        <div className="cw-list">
          {queueItems.map((q) => (
            <div key={q.id} className="cw-list-item" onClick={() => callNumber(q.customer_phone)}>
              <div className="cw-list-item__icon cw-list-item__icon--missed">
                <PhoneFilled />
              </div>
              <div className="cw-list-item__info">
                <div className="cw-list-item__primary">{q.customer_phone}</div>
                <div className="cw-list-item__secondary">
                  {q.reason} · {fmtTime(q.created_at)}
                </div>
              </div>
              <PhoneOutlined className="cw-list-item__call" />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const TABS: { key: TabKey; icon: React.ReactNode; label: string; badge?: number }[] = [
    { key: "dialpad", icon: <AppstoreOutlined />, label: "Keypad" },
    { key: "recents", icon: <HistoryOutlined />, label: "Recents" },
    { key: "contacts", icon: <ContactsOutlined />, label: "Contacts" },
    { key: "queue", icon: <UnorderedListOutlined />, label: "Queue", badge: pendingCount },
  ];

  return (
    <div className="call-widget-container" style={dock.containerStyle}>
      {/* Standalone recent-job side widget — covers the pre-answer (ringing)
          window; hides once the merged notes popup shows the same job info. */}
      {showCallerPopup && (
        <div className="cw-side-popups" style={callerPopupStyle}>
          {renderCallerInfoPopup()}
        </div>
      )}
      {/* Merged call-notes widget (job info + notes) — independently
          draggable via its header. */}
      {renderCallNotesPopup()}
      {expanded && (
        <div
          ref={phoneRef}
          className={`cw-phone ${fullSize ? "cw-phone--full" : ""}${isDragging ? " cw-phone--dragging" : ""}`}
          style={panelPos ? { position: "fixed", left: panelPos.x, top: panelPos.y } : undefined}
        >
          {/* Top bar */}
          <div className="cw-phone__topbar" onMouseDown={handleTopbarMouseDown}>
            <div className="cw-phone__status">
              <span className="cw-phone__status-dot" style={{ background: statusColor }} />
              <span>{statusText}</span>
              {myPhoneNo && (
                <Typography.Text className="cw-phone__my-num" copyable>
                  {myPhoneNo}
                </Typography.Text>
              )}
            </div>
            <div className="cw-phone__topbar-actions">
              <button type="button" className="cw-phone__topbar-btn" onClick={() => setFullSize(!fullSize)}>
                {fullSize ? <CompressOutlined /> : <ExpandOutlined />}
              </button>
              <Tooltip title="Minimize" placement="bottom">
                <button type="button" className="cw-phone__topbar-btn" onClick={() => setExpanded(false)}>
                  <MinusOutlined />
                </button>
              </Tooltip>
              <Tooltip title="Close" placement="bottom">
                <button
                  type="button"
                  className="cw-phone__topbar-btn"
                  onClick={() => {
                    setExpanded(false);
                    setSmsTarget(null);
                  }}
                >
                  <CloseOutlined />
                </button>
              </Tooltip>
            </div>
          </div>

          {/* Reconnect bar */}
          {!twilioReady && !twilioConnecting && (
            <div className="cw-phone__reconnect" onClick={reconnect}>
              Tap to reconnect phone service
            </div>
          )}

          {/* Main content */}
          <div className="cw-phone__body">
            {hasActiveInteraction ? (
              renderCallScreen()
            ) : smsTarget ? (
              renderSmsChat()
            ) : (
              <>
                {activeTab === "dialpad" && renderDialpad()}
                {activeTab === "recents" && renderRecents()}
                {activeTab === "contacts" && renderContacts()}
                {activeTab === "queue" && renderQueue()}
              </>
            )}
          </div>

          {/* Tab bar (hidden during active call or SMS chat) */}
          {!hasActiveInteraction && !smsTarget && (
            <div className="cw-phone__tabbar">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`cw-phone__tab ${activeTab === t.key ? "cw-phone__tab--active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  <span className="cw-phone__tab-icon">
                    {t.icon}
                    {!!t.badge && t.badge > 0 && <span className="cw-phone__tab-badge">{t.badge}</span>}
                  </span>
                  <span className="cw-phone__tab-label">{t.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAB */}
      <Tooltip
        title={hasActiveInteraction ? "Call in progress" : statusText}
        placement={dock.side === "left" ? "right" : "left"}
        open={dock.dragging ? false : undefined}
      >
        <div
          className={`call-widget-fab ${hasActiveInteraction ? "call-widget-fab--active" : ""} ${
            incomingCalls.length > 0 ? "call-widget-fab--ringing" : ""
          }${dock.isDraggingThis ? " launcher-dock-fab--dragging" : ""}`}
          {...dock.fabProps}
          onClick={() => setExpanded(!expanded)}
        >
          {/* Count of wrap-ups still owed (expanded + strip) so a minimized
              widget never hides them. */}
          <Badge
            count={wrapupQueue.length + (wrapup ? 1 : 0)}
            size="small"
            color="#faad14"
            offset={[6, 6]}
            title={`${wrapupQueue.length + (wrapup ? 1 : 0)} wrap-up(s) waiting`}
          >
            <Badge dot color={statusColor} offset={[1, -1]}>
              <PhoneOutlined className="call-widget-fab__icon" />
            </Badge>
          </Badge>
        </div>
      </Tooltip>
    </div>
  );
};

export default CallWidget;
